import { useNativePreferences } from '../storage/preferences';
import { SkeletonBlock } from '../components/LoadingPrimitives';
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Animated,
  Easing,
  FlatList,
  Image,
  InteractionManager,
  Platform,
  Pressable,
  StyleProp,
  StyleSheet,
  Text,
  View,
  ViewStyle,
} from "react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import { isTelevisionRuntime } from "../utils/platformCapabilities";
import { AnimeLocalization, getAnimeLocalization } from "../utils/animeLocalization";
import { SafeAreaView } from "react-native-safe-area-context";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import Svg, {
  Defs,
  LinearGradient,
  RadialGradient,
  Rect,
  Stop,
} from "react-native-svg";
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  CircleCheck,
  Play,
  RotateCcw,
} from "lucide-react-native";
import { RootStackParamList } from "../navigation/types";
import { MatureTopNav, useBottomNavInset } from "../components/MatureTopNav";
import { FocusablePressable } from "../components/FocusablePressable";
import { MediaCard } from "../components/MediaCard";
import { LandscapeMediaCard } from "../components/LandscapeMediaCard";
import { ItemAdminActions } from "../components/ItemAdminActions";
import {
  getEpisodes,
  getItem,
  getPlayerPlaybackInfo,
  getSeasons,
  getSimilarItems,
  imageSource,
  JellyfinItem,
  markItemPlayed,
  markItemUnplayed,
} from "../api/jellyfin";
import { useSession } from "../session/SessionProvider";
import {
  getAccentPresentation,
  getCachedImageAccent,
  getColorChannels,
  getImageAccent,
} from "../utils/imageAccent";
import {
  getPlaybackSourceLabel,
  PlaybackSource,
} from "../utils/playbackSourcePresentation";
import { usePlayerSources } from "../player/usePlayerSources";
import {
  PLAYER_PLAYBACK_INFO_STALE_MS,
  PLAYER_SOURCE_CATALOG_STALE_MS,
  playerPlaybackInfoQueryKey,
  playerSourceCatalogQueryKey,
} from "../player/playbackQueryKeys";
import { colors, systemSansFont } from "../theme/tokens";
import { MusicItemView } from "./MusicItemView";

type Props = NativeStackScreenProps<RootStackParamList, "Item">;
const pointerRowControls =
  !isTelevisionRuntime() && ["web", "macos", "windows", "linux"].includes(Platform.OS);
const technicalTagPrefix =
  /^(?:_|provider[.:]|remux[.:]|jellyfin[.:]|source[.:]|metadata[.:]|tmdb[.:]|imdb[.:]|tvdb[.:])/i;

function getVisibleEpisodeCount(compact: boolean, screenWidth: number) {
  if (compact) return 1;
  if (screenWidth >= 1600) return 5;
  if (screenWidth >= 1200) return 4;
  return 3;
}

function getEpisodeCardWidth(compact: boolean, screenWidth: number) {
  const viewport = Math.max(0, screenWidth - (compact ? 36 : 112));
  const visibleCount = getVisibleEpisodeCount(compact, screenWidth);
  return Math.floor((viewport - (visibleCount - 1) * 10) / visibleCount);
}

function getEpisodePanelMinimumHeight(screenWidth: number) {
  const cardHeight = Math.round(
    getEpisodeCardWidth(false, screenWidth) * 0.5625,
  );
  // Panel padding, season controls, and row spacing consume about 82px before
  // the 16:9 artwork. Keep the established 290px minimum at every breakpoint.
  return Math.max(290, 82 + cardHeight);
}

type ItemActionPressableProps = Omit<
  React.ComponentProps<typeof FocusablePressable>,
  "style"
> & {
  reduceMotion: boolean;
  fill?: boolean;
  style?:
    | StyleProp<ViewStyle>
    | ((
        focused: boolean,
        pressed: boolean,
        hovered: boolean,
      ) => StyleProp<ViewStyle>);
};

function ItemActionPressable({
  reduceMotion,
  fill = false,
  style,
  onFocus,
  onBlur,
  onHoverIn,
  onHoverOut,
  onPressIn,
  onPressOut,
  ...props
}: ItemActionPressableProps) {
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const attention = useRef(new Animated.Value(0)).current;
  const compression = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(attention, {
      toValue: focused || hovered ? 1 : 0,
      duration: reduceMotion ? 0 : focused || hovered ? 110 : 70,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [attention, focused, hovered, reduceMotion]);
  useEffect(() => {
    Animated.timing(compression, {
      toValue: pressed ? 1 : 0,
      duration: reduceMotion ? 0 : pressed ? 50 : 70,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
    }).start();
  }, [compression, pressed, reduceMotion]);
  const scale = Animated.multiply(
    attention.interpolate({
      inputRange: [0, 1],
      outputRange: [1, 1.012],
    }),
    compression.interpolate({
      inputRange: [0, 1],
      outputRange: [1, 0.985],
    }),
  );
  return (
    <Animated.View
      style={[
        fill && styles.actionMotionFill,
        {
          transform: [
            {
              translateY: attention.interpolate({
                inputRange: [0, 1],
                outputRange: [0, -1],
              }),
            },
            { scale },
          ],
        },
      ]}
    >
      <FocusablePressable
        {...props}
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        onHoverIn={(event) => {
          setHovered(true);
          onHoverIn?.(event);
        }}
        onHoverOut={(event) => {
          setHovered(false);
          onHoverOut?.(event);
        }}
        onPressIn={(event) => {
          setPressed(true);
          onPressIn?.(event);
        }}
        onPressOut={(event) => {
          setPressed(false);
          onPressOut?.(event);
        }}
        style={style}
      />
    </Animated.View>
  );
}

function runtime(ticks?: number) {
  if (!ticks) return "";
  const minutes = Math.round(ticks / 600000000);
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return hours > 0
    ? `${hours}h${remaining ? ` ${remaining}m` : ""}`
    : `${minutes}m`;
}

function ItemDetailsPanel({
  item,
  compact,
  screenWidth,
  panelHeight,
  isAdministrator,
  navigation,
}: {
  item: JellyfinItem;
  compact: boolean;
  screenWidth: number;
  panelHeight: number;
  isAdministrator: boolean;
  navigation: Props["navigation"];
}) {
  const cast =
    item.People?.filter(
      (person) => person.Type === "Actor" && person.Name,
    ).slice(0, 6) ?? [];
  const directors =
    item.People?.filter(
      (person) => person.Type === "Director" && person.Name,
    ) ?? [];
  const writers =
    item.People?.filter((person) => person.Type === "Writer" && person.Name) ??
    [];
  const about = [
    item.OriginalTitle && item.OriginalTitle !== item.Name
      ? { label: "Original title", value: item.OriginalTitle }
      : null,
    item.ProductionLocations?.length
      ? { label: "Production", value: item.ProductionLocations.join(", ") }
      : null,
    item.Status ? { label: "Status", value: item.Status } : null,
  ].filter((entry): entry is { label: string; value: string } =>
    Boolean(entry),
  );
  const regularTags = (item.Tags ?? []).filter(
    (tag) => !technicalTagPrefix.test(tag),
  );
  const technicalTags = (item.Tags ?? []).filter((tag) =>
    technicalTagPrefix.test(tag),
  );
  const detailMainWidth = Math.max(260, screenWidth - 112 - 2 - 56 - 280);
  const castEntryWidth = compact
    ? Math.max(240, screenWidth - 36)
    : Math.floor((detailMainWidth - 20) / 2);
  const sidebarCreditHeight = (count: number) =>
    count > 0 ? 23 + count * 18 + 14 : 0;
  const studioLineCapacity = Math.max(
    0,
    Math.floor(
      (panelHeight -
        28 -
        sidebarCreditHeight(directors.length) -
        sidebarCreditHeight(writers.length) -
        23) /
        18,
    ),
  );
  const studioCount =
    (item.Studios?.length ?? 0) <= studioLineCapacity
      ? (item.Studios?.length ?? 0)
      : Math.max(0, studioLineCapacity - 1);
  const visibleStudios = (item.Studios ?? []).slice(0, studioCount);
  const hiddenStudioCount = Math.max(
    0,
    (item.Studios?.length ?? 0) - visibleStudios.length,
  );
  return (
    <View style={[styles.detailsGrid, compact && styles.detailsGridCompact]}>
      <View style={styles.detailsMain}>
        {cast.length ? (
          <View style={styles.detailGroup}>
            <Text style={styles.detailHeading}>Cast</Text>
            <View style={styles.castGrid}>
              {cast.map((person, index) => (
                <FocusablePressable
                  key={person.Id ?? `${person.Name}-${index}`}
                  onPress={() =>
                    person.Id &&
                    navigation.navigate("Person", { personId: person.Id })
                  }
                  style={(focused, pressed, hovered) => [
                    styles.castEntry,
                    { width: castEntryWidth },
                    (focused || hovered) && styles.detailLinkActive,
                    pressed && styles.pressed,
                  ]}
                >
                  <Text numberOfLines={1} style={styles.detailLink}>
                    {person.Name}
                  </Text>
                  {person.Role ? (
                    <Text numberOfLines={1} style={styles.castRole}>
                      {person.Role}
                    </Text>
                  ) : null}
                </FocusablePressable>
              ))}
            </View>
          </View>
        ) : null}
        {about.length ? (
          <View style={styles.aboutGrid}>
            {about.map((entry) => (
              <View key={entry.label} style={styles.aboutEntry}>
                <Text style={styles.aboutLabel}>{entry.label}</Text>
                <Text numberOfLines={1} style={styles.aboutValue}>
                  {entry.value}
                </Text>
              </View>
            ))}
          </View>
        ) : null}
        {item.Genres?.length ? (
          <View style={styles.detailGroup}>
            <Text style={styles.detailHeading}>Genres</Text>
            <View style={styles.tagRow}>
              {item.Genres.map((genre) => {
                const genreId = item.GenreItems?.find(
                  (value) => value.Name?.toLowerCase() === genre.toLowerCase(),
                )?.Id;
                return (
                  <FocusablePressable
                    key={genre}
                    onPress={() =>
                      genreId &&
                      navigation.navigate("Genre", { genreId, name: genre })
                    }
                    style={(focused, pressed, hovered) => [
                      styles.detailTag,
                      (focused || hovered) && styles.detailTagActive,
                      pressed && styles.pressed,
                    ]}
                  >
                    <Text numberOfLines={1} style={styles.detailTagText}>
                      {genre}
                    </Text>
                  </FocusablePressable>
                );
              })}
            </View>
          </View>
        ) : null}
        {regularTags.length ? (
          <View style={styles.detailGroup}>
            <Text style={styles.detailHeading}>Tags</Text>
            <View style={styles.tagRow}>
              {regularTags.slice(0, 6).map((tag) => (
                <FocusablePressable
                  key={tag}
                  onPress={() => navigation.navigate("Search", { query: tag })}
                  style={(focused, pressed, hovered) => [
                    styles.detailTag,
                    (focused || hovered) && styles.detailTagActive,
                    pressed && styles.pressed,
                  ]}
                >
                  <Text numberOfLines={1} style={styles.detailTagText}>
                    {tag}
                  </Text>
                </FocusablePressable>
              ))}
            </View>
          </View>
        ) : null}
      </View>
      {!compact ? (
        <View style={styles.detailsSidebar}>
          {directors.length ? (
            <View style={styles.sidebarGroup}>
              <Text style={styles.detailHeading}>Directors</Text>
              {directors.map((person, index) => (
                <FocusablePressable
                  key={person.Id ?? `${person.Name}-${index}`}
                  onPress={() =>
                    person.Id &&
                    navigation.navigate("Person", { personId: person.Id })
                  }
                  style={(focused, pressed, hovered) => [
                    (focused || hovered) && styles.detailLinkActive,
                    pressed && styles.pressed,
                  ]}
                >
                  <Text style={styles.sidebarValue}>{person.Name}</Text>
                </FocusablePressable>
              ))}
            </View>
          ) : null}
          {writers.length ? (
            <View style={styles.sidebarGroup}>
              <Text style={styles.detailHeading}>Writers</Text>
              {writers.map((person, index) => (
                <FocusablePressable
                  key={person.Id ?? `${person.Name}-${index}`}
                  onPress={() =>
                    person.Id &&
                    navigation.navigate("Person", { personId: person.Id })
                  }
                  style={(focused, pressed, hovered) => [
                    (focused || hovered) && styles.detailLinkActive,
                    pressed && styles.pressed,
                  ]}
                >
                  <Text style={styles.sidebarValue}>{person.Name}</Text>
                </FocusablePressable>
              ))}
            </View>
          ) : null}
          {visibleStudios.length ? (
            <View style={styles.sidebarGroup}>
              <Text style={styles.detailHeading}>Production Companies</Text>
              {visibleStudios.map((studio, index) => (
                <Text
                  key={studio.Id ?? `${studio.Name}-${index}`}
                  style={styles.sidebarValue}
                >
                  {studio.Name}
                </Text>
              ))}
              {hiddenStudioCount > 0 ? (
                <Text style={styles.sidebarOverflow}>
                  {`+${hiddenStudioCount} more`}
                </Text>
              ) : null}
            </View>
          ) : null}
          {isAdministrator && technicalTags.length ? (
            <View style={[styles.sidebarGroup, styles.technicalTags]}>
              <Text style={styles.detailHeading}>Technical Tags</Text>
              <View style={styles.tagRow}>
                {technicalTags.slice(0, 3).map((tag) => (
                  <View key={tag} style={styles.detailTag}>
                    <Text numberOfLines={1} style={styles.detailTagText}>
                      {tag}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

function getSimilarCardWidth(
  compact: boolean,
  screenWidth: number,
  panelHeight: number,
) {
  const availableWidth = Math.max(118, screenWidth - (compact ? 52 : 128));
  const heightDrivenCardWidth = Math.max(
    150,
    Math.floor((panelHeight - 44) / 1.5),
  );
  const visibleCount = compact
    ? 1
    : Math.max(
        1,
        Math.ceil((availableWidth + 8) / (heightDrivenCardWidth + 8)),
      );
  return compact
    ? 118
    : Math.floor((availableWidth - (visibleCount - 1) * 8) / visibleCount);
}

function SimilarPosterPreloader({
  items,
  credentials,
  cardWidth,
  onReady,
}: {
  items: JellyfinItem[];
  credentials: NonNullable<ReturnType<typeof useSession>["credentials"]>;
  cardWidth: number;
  onReady: () => void;
}) {
  const targets = useMemo(() => items.slice(0, 10), [items]);
  const targetKey = targets.map((item) => item.Id ?? "").join(":");
  const settled = useRef(new Set<string>());
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  useEffect(() => {
    settled.current.clear();
  }, [targetKey]);
  const settle = (item: JellyfinItem, index: number) => {
    const key = item.Id ?? String(index);
    settled.current.add(key);
    if (settled.current.size >= targets.length) onReadyRef.current();
  };
  if (!targets.length) return null;
  const cardHeight = Math.round(cardWidth * 1.5);
  return (
    <View pointerEvents="none" style={styles.similarPosterPreloader}>
      {targets.map((item, index) => (
        <Image
          key={item.Id ?? `similar-preload-${index}`}
          source={imageSource(
            credentials,
            item,
            "Primary",
            Math.min(420, Math.round(cardWidth * 2)),
            Math.min(630, Math.round(cardHeight * 2)),
            82,
          )}
          onLoad={() => settle(item, index)}
          onError={() => settle(item, index)}
          style={styles.similarPosterPreloadImage}
        />
      ))}
    </View>
  );
}

function SimilarItemsPanel({
  items,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  credentials,
  navigation,
  compact,
  vertical,
  screenWidth,
  panelHeight,
  accent,
  instantImageReveal,
}: {
  items: JellyfinItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  credentials: NonNullable<ReturnType<typeof useSession>["credentials"]>;
  navigation: Props["navigation"];
  compact: boolean;
  vertical?: boolean;
  screenWidth: number;
  panelHeight: number;
  accent: string;
  instantImageReveal: boolean;
}) {
  const queryClient = useQueryClient();
  const listRef = useRef<FlatList<JellyfinItem>>(null);
  const [scrollX, setScrollX] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [controlsActive, setControlsActive] = useState(false);
  const [controlHovered, setControlHovered] = useState(false);
  const controlHoveredRef = useRef(false);
  const loadRequestedRef = useRef(false);
  const releaseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const accentRgb =
    accent.match(/\d+/g)?.slice(0, 3).join(",") ?? "115,115,115";
  const prepareItem = (candidate: JellyfinItem) => {
    if (!candidate.Id) return;
    void queryClient.prefetchQuery({
      queryKey: ["item", candidate.Id],
      queryFn: () => getItem(credentials, candidate.Id!),
      staleTime: 60_000,
    });
  };
  const cardWidth = getSimilarCardWidth(compact, screenWidth, panelHeight);
  const overflow =
    pointerRowControls && !compact && contentWidth > viewportWidth + 2;
  const activateControls = () => {
    if (releaseTimer.current) clearTimeout(releaseTimer.current);
    setControlsActive(true);
  };
  const releaseControls = () => {
    releaseTimer.current = setTimeout(() => {
      if (!controlHoveredRef.current) setControlsActive(false);
    }, 160);
  };
  const setControlIntent = (active: boolean) => {
    controlHoveredRef.current = active;
    setControlHovered(active);
  };
  useEffect(() => {
    if (!loadingMore) loadRequestedRef.current = false;
  }, [items.length, loadingMore]);
  const requestMore = () => {
    if (!hasMore || loadingMore || loadRequestedRef.current) return;
    loadRequestedRef.current = true;
    onLoadMore();
  };
  useEffect(
    () => () => {
      if (releaseTimer.current) clearTimeout(releaseTimer.current);
    },
    [],
  );
  const scroll = (direction: -1 | 1) => {
    const maxOffset = Math.max(0, contentWidth - viewportWidth);
    const nextOffset = Math.max(
      0,
      Math.min(
        maxOffset,
        scrollX + direction * Math.max(cardWidth + 8, viewportWidth * 0.8),
      ),
    );
    if (direction > 0 && maxOffset - nextOffset <= viewportWidth * 0.75)
      requestMore();
    listRef.current?.scrollToOffset({
      offset: nextOffset,
      animated: true,
    });
  };
  const renderControl = (direction: -1 | 1) => {
    const disabled =
      direction < 0
        ? scrollX <= 1
        : !hasMore && scrollX >= contentWidth - viewportWidth - 2;
    return (
      <FocusablePressable
        accessibilityLabel={
          direction < 0 ? "Previous similar titles" : "Next similar titles"
        }
        disabled={disabled}
        onHoverIn={() => {
          setControlIntent(true);
          activateControls();
        }}
        onHoverOut={() => {
          setControlIntent(false);
          releaseControls();
        }}
        onFocus={() => {
          setControlIntent(true);
          activateControls();
        }}
        onBlur={() => {
          setControlIntent(false);
          releaseControls();
        }}
        onPress={(event) => {
          event.stopPropagation();
          scroll(direction);
        }}
        style={(focused, pressed, hovered) => [
          styles.similarScrollButton,
          { height: Math.round(cardWidth * 1.5) },
          direction < 0 ? styles.similarScrollLeft : styles.similarScrollRight,
          disabled && styles.seasonScrollDisabled,
          (focused || hovered) && {
            backgroundColor: `rgba(${accentRgb},0.72)`,
          },
          pressed && styles.pressed,
        ]}
      >
        {direction < 0 ? (
          <ChevronLeft color={colors.text} size={28} />
        ) : (
          <ChevronRight color={colors.text} size={28} />
        )}
      </FocusablePressable>
    );
  };
  return (
    <View
      style={styles.similarInteractionRegion}
      onPointerEnter={activateControls}
      onPointerLeave={releaseControls}
    >
      <View style={[styles.glass, compact && styles.glassCompact]}>
        {loading && !items.length ? (
          <View style={styles.similarStatus}>
            <ActivityIndicator color={accent} size="small" />
            <Text style={styles.similarStatusText}>Loading similar titles</Text>
          </View>
        ) : items.length ? vertical ? (
          <FlatList
            data={items}
            keyExtractor={(value, index) => value.Id ?? `similar-${index}`}
            scrollEnabled={false}
            contentContainerStyle={styles.episodeListColumn}
            renderItem={({ item }) => (
              <MediaListRow
                credentials={credentials}
                item={item}
                rowWidth={screenWidth - 40}
                accent={accent}
                onPress={() =>
                  item.Id &&
                  navigation.navigate("Item", {
                    itemId: item.Id,
                    previewItem: item,
                  })
                }
              />
            )}
          />
        ) : (
          <View style={styles.similarListViewport}>
            <FlatList
              ref={listRef}
              horizontal
              data={items}
              keyExtractor={(value, index) => value.Id ?? `similar-${index}`}
              showsHorizontalScrollIndicator={false}
              onLayout={(event) => setViewportWidth(event.nativeEvent.layout.width)}
              onContentSizeChange={(width) => setContentWidth(width)}
              onScroll={(event) => setScrollX(event.nativeEvent.contentOffset.x)}
              scrollEventThrottle={16}
              onEndReached={hasMore ? requestMore : undefined}
              onEndReachedThreshold={0.65}
              contentContainerStyle={styles.similarRow}
              renderItem={({ item }) => (
                <MediaCard
                  item={item}
                  credentials={credentials}
                  width={cardWidth}
                  gap={0}
                  hideLabels
                  instantImageReveal={instantImageReveal}
                  hovered={controlHovered ? false : undefined}
                  onFocusChange={(value) => {
                    if (value) prepareItem(item);
                    value ? activateControls() : releaseControls();
                  }}
                  onHoverChange={(value) => {
                    if (value) prepareItem(item);
                    value ? activateControls() : releaseControls();
                  }}
                  onPress={() =>
                    item.Id &&
                    navigation.navigate("Item", {
                      itemId: item.Id,
                      previewItem: item,
                    })
                  }
                  onPlay={() =>
                    item.Id && navigation.navigate("Player", { itemId: item.Id })
                  }
                />
              )}
            />
          </View>
        ) : (
          <View style={[styles.similarStatus, styles.similarStatusStack]}>
            <Text style={styles.similarEmptyTitle}>Nothing similar yet</Text>
            <Text style={styles.similarStatusText}>
              This library has no related titles to show for this item.
            </Text>
          </View>
        )}
        {loadingMore ? (
          <View pointerEvents="none" style={styles.similarLoadingMore}>
            <ActivityIndicator color={accent} size="small" />
            <Text style={styles.similarLoadingMoreText}>Loading more</Text>
          </View>
        ) : null}
      </View>
      {overflow && controlsActive ? (
        <>
          {renderControl(-1)}
          {renderControl(1)}
        </>
      ) : null}
    </View>
  );
}

function MediaListRow({
  credentials,
  item,
  rowWidth,
  accent,
  focusId,
  focusGroup,
  focusDestinations,
  onPress,
  animeLocalization,
}: {
  credentials: NonNullable<ReturnType<typeof useSession>["credentials"]>;
  item: JellyfinItem;
  rowWidth: number;
  accent: string;
  focusId?: string;
  focusGroup?: string;
  focusDestinations?: import('../tvNavigation/core').FocusDestinations;
  onPress: () => void;
  animeLocalization?: AnimeLocalization;
}) {
  const [imageFailed, setImageFailed] = useState(false);
  const isEpisode = item.Type === "Episode";
  const thumbWidth = Math.round(rowWidth * 0.3);
  const thumbHeight = Math.round(thumbWidth * 0.5625);
  const kind = imageFailed
    ? "Primary"
    : isEpisode
      ? item.ImageTags?.Thumb
        ? "Thumb"
        : "Primary"
      : item.BackdropImageTags?.length
        ? "Backdrop"
        : "Primary";
  const episodeName = isEpisode
    ? (item.Name ?? "").replace(/^S\d+E\d+\s*[-–:]\s*/i, "")
    : "";
  const played = item.UserData?.Played === true;
  const progressPercent =
    typeof item.UserData?.PlayedPercentage === "number" &&
    item.UserData.PlayedPercentage > 0
      ? Math.min(item.UserData.PlayedPercentage, 100)
      : 0;
  const meta =
    item.Type === "Series" && typeof item.ChildCount === "number"
      ? `${item.ChildCount} ${item.ChildCount === 1 ? "season" : "seasons"}`
      : runtime(item.RunTimeTicks) ||
        (item.ProductionYear ? String(item.ProductionYear) : "");
  return (
    <FocusablePressable
      focusId={focusId}
      focusGroup={focusGroup}
      focusDestinations={focusDestinations}
      focusRole="card"
      accessibilityLabel={
        isEpisode
          ? `Episode ${item.IndexNumber ?? ""}: ${episodeName}`
          : (item.Name ?? "Untitled")
      }
      onPress={onPress}
      style={(focused, pressed, hovered) => [
        styles.episodeListRow,
        (focused || hovered) && styles.episodeListRowActive,
        pressed && styles.pressed,
      ]}
    >
      <View
        style={[
          styles.episodeListThumb,
          { width: thumbWidth, height: thumbHeight },
        ]}
      >
        <Image
          source={imageSource(
            credentials,
            item,
            kind,
            thumbWidth * 2,
            thumbHeight * 2,
          )}
          onError={() => setImageFailed(true)}
          resizeMode="cover"
          style={StyleSheet.absoluteFill}
        />
        {animeLocalization ? <View pointerEvents="none" style={styles.episodeListLocalizationBadge}><Text style={styles.episodeListLocalizationText}>{animeLocalization}</Text></View> : null}
        {played ? (
          <View style={styles.episodeListWatched}>
            <Check color="#fff" size={11} strokeWidth={3.5} />
          </View>
        ) : null}
      </View>
      <View style={styles.episodeListBody}>
        <View style={styles.episodeListTitleRow}>
          <Text numberOfLines={1} style={styles.episodeListTitle}>
            {isEpisode && item.IndexNumber != null ? (
              <>
                <Text style={{ color: accent }}>{item.IndexNumber}</Text>
                {" – "}
              </>
            ) : null}
            {isEpisode ? episodeName || "Untitled" : item.Name ?? "Untitled"}
          </Text>
          {meta ? (
            <Text style={styles.episodeListDurationInline}>{meta}</Text>
          ) : null}
        </View>
        {item.Overview ? (
          <Text numberOfLines={2} style={styles.episodeListOverview}>
            {item.Overview}
          </Text>
        ) : null}
      </View>
      {progressPercent > 0 ? (
        <View style={styles.episodeListTrack}>
          <View
            style={[
              styles.episodeListProgress,
              { width: Math.round((rowWidth * progressPercent) / 100) },
            ]}
          />
        </View>
      ) : null}
    </FocusablePressable>
  );
}

function SeasonEpisodes({
  credentials,
  navigation,
  seriesId,
  initialSeasonId,
  initialEpisodeId,
  compact,
  vertical,
  screenWidth,
  accent,
  reduceMotion,
  animeLocalization,
}: {
  credentials: NonNullable<ReturnType<typeof useSession>["credentials"]>;
  navigation: Props["navigation"];
  seriesId: string;
  initialSeasonId?: string;
  initialEpisodeId?: string;
  compact: boolean;
  vertical?: boolean;
  screenWidth: number;
  accent: string;
  reduceMotion: boolean;
  animeLocalization?: AnimeLocalization;
}) {
  const queryClient = useQueryClient();
  const accentRgb =
    accent.match(/\d+/g)?.slice(0, 3).join(",") ?? "115,115,115";
  const seasonListRef = useRef<FlatList<JellyfinItem>>(null);
  const [seasonScrollX, setSeasonScrollX] = useState(0);
  const [seasonContentWidth, setSeasonContentWidth] = useState(0);
  const [seasonViewportWidth, setSeasonViewportWidth] = useState(0);
  const episodeListRef = useRef<FlatList<JellyfinItem>>(null);
  const [episodeScrollX, setEpisodeScrollX] = useState(0);
  const [episodeContentWidth, setEpisodeContentWidth] = useState(0);
  const [episodeViewportWidth, setEpisodeViewportWidth] = useState(0);
  const [seasonControlsActive, setSeasonControlsActive] = useState(false);
  const [episodeControlsActive, setEpisodeControlsActive] = useState(false);
  const [episodeControlHovered, setEpisodeControlHovered] = useState(false);
  const episodeControlHoveredRef = useRef(false);
  const episodeRowOpacity = useRef(new Animated.Value(1)).current;
  const episodeRowOffset = useRef(new Animated.Value(0)).current;
  const seasonControlsTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const episodeControlsTimer = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const prepareEpisode = (episode: JellyfinItem) => {
    if (!episode.Id) return;
    void queryClient.prefetchQuery({
      queryKey: ["item", episode.Id],
      queryFn: () => getItem(credentials, episode.Id!),
      staleTime: 60_000,
    });
  };
  const seasonsQuery = useQuery({
    queryKey: ["item-seasons", seriesId],
    queryFn: () => getSeasons(credentials, seriesId),
  });
  const seasons = useMemo(
    () =>
      [...(seasonsQuery.data ?? [])].sort((left, right) => {
        const leftIsExtras =
          left.IndexNumber === 0 || /^Season 0$/i.test(left.Name ?? "");
        const rightIsExtras =
          right.IndexNumber === 0 || /^Season 0$/i.test(right.Name ?? "");
        if (leftIsExtras !== rightIsExtras) return leftIsExtras ? 1 : -1;
        return (left.IndexNumber ?? Number.MAX_SAFE_INTEGER) -
          (right.IndexNumber ?? Number.MAX_SAFE_INTEGER);
      }),
    [seasonsQuery.data],
  );
  const [seasonId, setSeasonId] = useState(initialSeasonId ?? "");
  useEffect(() => {
    if (seasons.length && !seasons.some((season) => season.Id === seasonId))
      setSeasonId(
        initialSeasonId &&
          seasons.some((season) => season.Id === initialSeasonId)
          ? initialSeasonId
          : (seasons[0].Id ?? ""),
      );
  }, [initialSeasonId, seasonId, seasons]);
  useEffect(() => {
    const selectedIndex = seasons.findIndex((season) => season.Id === seasonId);
    if (
      selectedIndex < 0 ||
      seasonViewportWidth <= 0 ||
      seasonContentWidth <= 0
    )
      return;
    const buttonWidth = 116;
    const itemStride = 126;
    const centeredOffset =
      selectedIndex * itemStride - (seasonViewportWidth - buttonWidth) / 2;
    seasonListRef.current?.scrollToOffset({
      offset: Math.max(
        0,
        Math.min(seasonContentWidth - seasonViewportWidth, centeredOffset),
      ),
      animated: false,
    });
  }, [seasonContentWidth, seasonId, seasonViewportWidth, seasons]);
  const episodesQuery = useQuery({
    queryKey: ["item-episodes", seriesId, seasonId],
    queryFn: () => getEpisodes(credentials, seriesId, seasonId),
    enabled: Boolean(seasonId),
    placeholderData: (previous) => previous,
  });
  useEffect(() => {
    if (episodesQuery.isPlaceholderData) return;
    episodeRowOpacity.setValue(reduceMotion ? 1 : 0.55);
    episodeRowOffset.setValue(reduceMotion ? 0 : 6);
    Animated.parallel([
      Animated.timing(episodeRowOpacity, {
        toValue: 1,
        duration: reduceMotion ? 0 : 160,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(episodeRowOffset, {
        toValue: 0,
        duration: reduceMotion ? 0 : 160,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, [
    episodeRowOpacity,
    episodeRowOffset,
    episodesQuery.data,
    episodesQuery.isPlaceholderData,
    reduceMotion,
    seasonId,
  ]);
  const seasonOverflow =
    pointerRowControls &&
    !compact &&
    seasonContentWidth > seasonViewportWidth + 2;
  const episodeOverflow =
    pointerRowControls &&
    !compact &&
    episodeContentWidth > episodeViewportWidth + 2;
  const episodeViewport = Math.max(0, screenWidth - (compact ? 36 : 112));
  const visibleEpisodeCount = getVisibleEpisodeCount(compact, screenWidth);
  const episodeCardWidth = getEpisodeCardWidth(compact, screenWidth);
  const episodeEntryRef = useRef({ seriesId, episodeId: initialEpisodeId });
  if (episodeEntryRef.current.seriesId !== seriesId) {
    episodeEntryRef.current = { seriesId, episodeId: initialEpisodeId };
  } else if (!episodeEntryRef.current.episodeId && initialEpisodeId) {
    episodeEntryRef.current.episodeId = initialEpisodeId;
  }
  const entryEpisodeId = episodeEntryRef.current.episodeId;
  const initialEpisodeIndex = (episodesQuery.data ?? []).findIndex(
    (episode) => episode.Id === entryEpisodeId,
  );
  const episodeEndAlignmentSpace =
    !vertical && initialEpisodeIndex > 0
      ? Math.max(0, episodeViewport - episodeCardWidth)
      : 0;
  const firstEpisodeFocusId = episodesQuery.data?.[0]?.Id
    ? `item.episode.${seriesId}.${seasonId}.${episodesQuery.data[0].Id}`
    : undefined;
  const selectedSeasonFocusId = `item.season.${seriesId}.${seasonId}`;
  const episodeAlignmentKey = useRef("");
  useEffect(() => {
    if (
      vertical ||
      !entryEpisodeId ||
      initialEpisodeIndex < 0 ||
      episodesQuery.isPlaceholderData
    )
      return;
    const key = `${seasonId}:${entryEpisodeId}:${episodeCardWidth}`;
    if (episodeAlignmentKey.current === key) return;
    episodeAlignmentKey.current = key;
    const timer = setTimeout(() => {
      episodeListRef.current?.scrollToOffset({
        offset: initialEpisodeIndex * (episodeCardWidth + 10),
        animated: false,
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [
    episodeCardWidth,
    episodesQuery.isPlaceholderData,
    entryEpisodeId,
    initialEpisodeIndex,
    seasonId,
    vertical,
  ]);
  const selectSeason = (nextSeasonId: string) => {
    if (!nextSeasonId || nextSeasonId === seasonId) return;
    episodeRowOpacity.stopAnimation();
    Animated.timing(episodeRowOpacity, {
      toValue: 0.38,
      duration: reduceMotion ? 0 : 80,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
    }).start();
    setSeasonId(nextSeasonId);
  };
  const selectedSeasonName =
    seasons.find((season) => season.Id === seasonId)?.Name ?? "season";
  const scrollSeasons = (direction: -1 | 1) =>
    seasonListRef.current?.scrollToOffset({
      offset: Math.max(
        0,
        Math.min(
          seasonContentWidth - seasonViewportWidth,
          seasonScrollX + direction * Math.max(126, seasonViewportWidth * 0.72),
        ),
      ),
      animated: true,
    });
  const scrollEpisodes = (direction: -1 | 1) =>
    episodeListRef.current?.scrollToOffset({
      offset: Math.max(
        0,
        Math.min(
          episodeContentWidth - episodeViewportWidth,
          episodeScrollX +
            direction * Math.max(318, episodeViewportWidth * 0.76),
        ),
      ),
      animated: true,
    });
  const activateSeasonControls = () => {
    if (seasonControlsTimer.current) clearTimeout(seasonControlsTimer.current);
    setSeasonControlsActive(true);
  };
  const releaseSeasonControls = () => {
    seasonControlsTimer.current = setTimeout(
      () => setSeasonControlsActive(false),
      160,
    );
  };
  const activateEpisodeControls = () => {
    if (episodeControlsTimer.current)
      clearTimeout(episodeControlsTimer.current);
    setEpisodeControlsActive(true);
  };
  const releaseEpisodeControls = () => {
    episodeControlsTimer.current = setTimeout(
      () => {
        if (!episodeControlHoveredRef.current)
          setEpisodeControlsActive(false);
      },
      160,
    );
  };
  const setEpisodeControlIntent = (active: boolean) => {
    episodeControlHoveredRef.current = active;
    setEpisodeControlHovered(active);
  };
  useEffect(
    () => () => {
      if (seasonControlsTimer.current)
        clearTimeout(seasonControlsTimer.current);
      if (episodeControlsTimer.current)
        clearTimeout(episodeControlsTimer.current);
    },
    [],
  );
  return (
    <View style={styles.episodesPanel}>
      <View
        style={[styles.seasonScroller, compact && styles.rowScrollerCompact]}
        onPointerEnter={activateSeasonControls}
        onPointerLeave={releaseSeasonControls}
      >
        <FlatList
          ref={seasonListRef}
          horizontal
          data={seasons}
          keyExtractor={(season, index) => season.Id ?? `season-${index}`}
          showsHorizontalScrollIndicator={false}
          onLayout={(event) =>
            setSeasonViewportWidth(event.nativeEvent.layout.width)
          }
          onContentSizeChange={(contentWidth) =>
            setSeasonContentWidth(contentWidth)
          }
          onScroll={(event) =>
            setSeasonScrollX(event.nativeEvent.contentOffset.x)
          }
          scrollEventThrottle={16}
          contentContainerStyle={styles.seasonRow}
          renderItem={({ item: season }) => {
            const selected = season.Id === seasonId;
            const seasonName =
              season.IndexNumber === 0 || /^Season 0$/i.test(season.Name ?? "")
                ? "Extras"
                : (season.Name ?? "Season");
            return (
              <FocusablePressable
                focusId={`item.season.${seriesId}.${season.Id ?? seasonName}`}
                focusGroup={`item.seasons.${seriesId}`}
                focusRole="tab"
                focusDestinations={firstEpisodeFocusId ? { down: firstEpisodeFocusId } : undefined}
                accessibilityLabel={`Show ${seasonName}`}
                accessibilityState={{ selected }}
                onFocus={activateSeasonControls}
                onBlur={releaseSeasonControls}
                onHoverIn={activateSeasonControls}
                onHoverOut={releaseSeasonControls}
                onPress={() => selectSeason(season.Id ?? "")}
                style={(focused, pressed, hovered) => [
                  styles.seasonButton,
                  selected && styles.seasonButtonSelected,
                  (focused || hovered) && styles.seasonButtonActive,
                  pressed && styles.pressed,
                ]}
              >
                <Text
                  style={[
                    styles.seasonButtonText,
                    selected && styles.seasonButtonTextSelected,
                  ]}
                >
                  {seasonName}
                </Text>
                {selected ? (
                  <View
                    pointerEvents="none"
                    style={[
                      styles.seasonSelectedInset,
                      { backgroundColor: accent },
                    ]}
                  />
                ) : null}
              </FocusablePressable>
            );
          }}
        />
        {seasonOverflow && seasonControlsActive ? (
          <>
            <FocusablePressable
              accessibilityLabel="Previous seasons"
              disabled={seasonScrollX <= 1}
              onHoverIn={() => {
                activateSeasonControls();
              }}
              onHoverOut={() => {
                releaseSeasonControls();
              }}
              onFocus={activateSeasonControls}
              onBlur={releaseSeasonControls}
              onPress={(event) => {
                event.stopPropagation();
                scrollSeasons(-1);
              }}
              style={(focused, pressed, hovered) => [
                styles.seasonScrollButton,
                styles.seasonScrollLeft,
                seasonScrollX <= 1 && styles.seasonScrollDisabled,
                (focused || hovered) && {
                  backgroundColor: `rgba(${accentRgb},0.72)`,
                },
                pressed && styles.pressed,
              ]}
            >
              <ChevronLeft color={colors.text} size={22} />
            </FocusablePressable>
            <FocusablePressable
              accessibilityLabel="Next seasons"
              disabled={
                seasonScrollX >= seasonContentWidth - seasonViewportWidth - 2
              }
              onHoverIn={() => {
                activateSeasonControls();
              }}
              onHoverOut={() => {
                releaseSeasonControls();
              }}
              onFocus={activateSeasonControls}
              onBlur={releaseSeasonControls}
              onPress={(event) => {
                event.stopPropagation();
                scrollSeasons(1);
              }}
              style={(focused, pressed, hovered) => [
                styles.seasonScrollButton,
                styles.seasonScrollRight,
                seasonScrollX >= seasonContentWidth - seasonViewportWidth - 2 &&
                  styles.seasonScrollDisabled,
                (focused || hovered) && {
                  backgroundColor: `rgba(${accentRgb},0.72)`,
                },
                pressed && styles.pressed,
              ]}
            >
              <ChevronRight color={colors.text} size={22} />
            </FocusablePressable>
          </>
        ) : null}
      </View>
      {episodesQuery.isLoading ? (
        <View
          style={[styles.episodeScroller, compact && styles.rowScrollerCompact]}
        >
          <FlatList
            horizontal={!vertical}
            scrollEnabled={false}
            data={Array.from(
              { length: vertical ? 3 : visibleEpisodeCount },
              (_, index) => index,
            )}
            keyExtractor={(index) => `episode-placeholder-${index}`}
            contentContainerStyle={
              vertical ? styles.episodeListColumn : styles.episodeRow
            }
            renderItem={() =>
              vertical ? (
                <View style={styles.episodeListRow}>
                  <View
                    style={[
                      styles.episodeSkeletonImage,
                      {
                        width: Math.round(episodeCardWidth * 0.3),
                        height: Math.round(episodeCardWidth * 0.3 * 0.5625),
                      },
                    ]}
                  />
                  <View style={styles.episodeListBody}>
                    <View style={styles.episodeSkeletonTitle} />
                    <View
                      style={[
                        styles.episodeSkeletonTitle,
                        styles.episodeSkeletonLine,
                      ]}
                    />
                  </View>
                </View>
              ) : (
                <View style={{ width: episodeCardWidth }}>
                  <View
                    style={[
                      styles.episodeSkeletonImage,
                      {
                        width: episodeCardWidth,
                        height: Math.round(episodeCardWidth * 0.5625),
                      },
                    ]}
                  />
                  <View style={styles.episodeSkeletonTitle} />
                </View>
              )
            }
          />
          <View
            pointerEvents="none"
            style={[
              styles.seasonLoadingBadge,
              { left: Math.max(0, (episodeViewport - 190) / 2) },
            ]}
          >
            <ActivityIndicator color={accent} size="small" />
            <Text style={styles.seasonLoadingText}>
              {`Loading ${selectedSeasonName}`}
            </Text>
          </View>
        </View>
      ) : !(episodesQuery.data?.length) ? (
        <View
          style={[
            styles.episodeEmptyState,
            compact && styles.rowScrollerCompact,
          ]}
        >
          <Text style={styles.episodeEmptyTitle}>No episodes available</Text>
          <Text style={styles.episodeEmptyText}>
            {`${selectedSeasonName} does not contain any playable episodes.`}
          </Text>
        </View>
      ) : (
        <View
          style={[styles.episodeScroller, compact && styles.rowScrollerCompact]}
          onPointerEnter={activateEpisodeControls}
          onPointerLeave={releaseEpisodeControls}
        >
          <Animated.View
            style={{
              opacity: episodeRowOpacity,
              transform: [{ translateY: episodeRowOffset }],
            }}
          >
            <FlatList
              ref={episodeListRef}
              horizontal={!vertical}
              scrollEnabled={!vertical}
              data={episodesQuery.data ?? []}
              keyExtractor={(episode, index) =>
                episode.Id ?? `episode-${index}`
              }
              showsHorizontalScrollIndicator={false}
              onLayout={(event) =>
                setEpisodeViewportWidth(event.nativeEvent.layout.width)
              }
              onContentSizeChange={(contentWidth) =>
                setEpisodeContentWidth(contentWidth)
              }
              onScroll={(event) =>
                setEpisodeScrollX(event.nativeEvent.contentOffset.x)
              }
              scrollEventThrottle={16}
              contentContainerStyle={
                vertical
                  ? styles.episodeListColumn
                  : [
                      styles.episodeRow,
                      episodeEndAlignmentSpace > 0 && {
                        paddingRight: episodeEndAlignmentSpace,
                      },
                    ]
              }
              renderItem={({ item: episode }) =>
                vertical ? (
                  <MediaListRow
                    credentials={credentials}
                    item={episode}
                    rowWidth={episodeCardWidth}
                    accent={accent}
                    focusId={`item.episode.${seriesId}.${seasonId}.${episode.Id ?? episode.IndexNumber ?? 'unknown'}`}
                    focusGroup={`item.episodes.${seriesId}.${seasonId}`}
                    focusDestinations={{ up: selectedSeasonFocusId }}
                    animeLocalization={animeLocalization}
                    onPress={() =>
                      episode.Id &&
                      navigation.navigate("Item", {
                        itemId: episode.Id,
                        previewItem: episode,
                        transition: "none",
                      })
                    }
                  />
                ) : (
                  <LandscapeMediaCard
                    item={episode}
                    credentials={credentials}
                    width={episodeCardWidth}
                    itemDetail
                    itemDetailAccent={accent}
                    focusId={`item.episode.${seriesId}.${seasonId}.${episode.Id ?? episode.IndexNumber ?? 'unknown'}`}
                    focusGroup={`item.episodes.${seriesId}.${seasonId}`}
                    focusDestinations={{ up: selectedSeasonFocusId }}
                    animeLocalization={animeLocalization}
                    hovered={episodeControlHovered ? false : undefined}
                    onFocusChange={(value) => {
                      if (value) prepareEpisode(episode);
                      value
                        ? activateEpisodeControls()
                        : releaseEpisodeControls();
                    }}
                    onHoverChange={(value) => {
                      if (value) prepareEpisode(episode);
                      value
                        ? activateEpisodeControls()
                        : releaseEpisodeControls();
                    }}
                    onPress={() =>
                      episode.Id &&
                      navigation.navigate("Item", {
                        itemId: episode.Id,
                        previewItem: episode,
                        transition: "none",
                      })
                    }
                    onPlay={() =>
                      episode.Id &&
                      navigation.navigate("Player", { itemId: episode.Id })
                    }
                  />
                )
              }
            />
          </Animated.View>
          {episodesQuery.isPlaceholderData ? (
            <View
              pointerEvents="none"
              style={[
                styles.seasonLoadingBadge,
                { left: Math.max(0, (episodeViewport - 190) / 2) },
              ]}
            >
              <ActivityIndicator color={accent} size="small" />
              <Text style={styles.seasonLoadingText}>
                {`Loading ${selectedSeasonName}`}
              </Text>
            </View>
          ) : null}
          {episodeOverflow && episodeControlsActive ? (
            <>
              <FocusablePressable
                accessibilityLabel="Previous episodes"
                disabled={episodeScrollX <= 1}
                onHoverIn={() => {
                  setEpisodeControlIntent(true);
                  activateEpisodeControls();
                }}
                onHoverOut={() => {
                  setEpisodeControlIntent(false);
                  releaseEpisodeControls();
                }}
                onFocus={() => {
                  setEpisodeControlIntent(true);
                  activateEpisodeControls();
                }}
                onBlur={() => {
                  setEpisodeControlIntent(false);
                  releaseEpisodeControls();
                }}
                onPress={(event) => {
                  event.stopPropagation();
                  scrollEpisodes(-1);
                }}
                style={(focused, pressed, hovered) => [
                  styles.episodeScrollButton,
                  { height: Math.round(episodeCardWidth * 0.5625) },
                  styles.episodeScrollLeft,
                  episodeScrollX <= 1 && styles.seasonScrollDisabled,
                  (focused || hovered) && {
                    backgroundColor: `rgba(${accentRgb},0.72)`,
                  },
                  pressed && styles.pressed,
                ]}
              >
                <ChevronLeft color={colors.text} size={28} />
              </FocusablePressable>
              <FocusablePressable
                accessibilityLabel="Next episodes"
                disabled={
                  episodeScrollX >=
                  episodeContentWidth - episodeViewportWidth - 2
                }
                onHoverIn={() => {
                  setEpisodeControlIntent(true);
                  activateEpisodeControls();
                }}
                onHoverOut={() => {
                  setEpisodeControlIntent(false);
                  releaseEpisodeControls();
                }}
                onFocus={() => {
                  setEpisodeControlIntent(true);
                  activateEpisodeControls();
                }}
                onBlur={() => {
                  setEpisodeControlIntent(false);
                  releaseEpisodeControls();
                }}
                onPress={(event) => {
                  event.stopPropagation();
                  scrollEpisodes(1);
                }}
                style={(focused, pressed, hovered) => [
                  styles.episodeScrollButton,
                  { height: Math.round(episodeCardWidth * 0.5625) },
                  styles.episodeScrollRight,
                  episodeScrollX >=
                    episodeContentWidth - episodeViewportWidth - 2 &&
                    styles.seasonScrollDisabled,
                  (focused || hovered) && {
                    backgroundColor: `rgba(${accentRgb},0.72)`,
                  },
                  pressed && styles.pressed,
                ]}
              >
                <ChevronRight color={colors.text} size={28} />
              </FocusablePressable>
            </>
          ) : null}
        </View>
      )}
    </View>
  );
}

export function ItemScreen({ navigation, route }: Props) {
  const { width, height, referenceScale } = useReferenceWindowDimensions();
  const compact = width <= 760;
  const wide = width >= 1024;
  const televisionRuntime = isTelevisionRuntime();
  const { credentials, user } = useSession();
  const queryClient = useQueryClient();
  const [deferredSectionsReady, setDeferredSectionsReady] = useState(!televisionRuntime);
  const itemQuery = useQuery({
    queryKey: ["item", route.params.itemId],
    queryFn: () => getItem(credentials!, route.params.itemId),
    // Preview metadata is already available during TV route entry. Defer the
    // full response until the transition settles to avoid a large rerender in
    // the first frames; deep links without a preview still show the skeleton.
    enabled: Boolean(credentials && (!televisionRuntime || deferredSectionsReady)),
    placeholderData: route.params.previewItem,
  });
  const item = itemQuery.data;
  const seriesArtworkQuery = useQuery({
    queryKey: ["item-series-artwork", item?.SeriesId],
    queryFn: () => getItem(credentials!, item!.SeriesId!),
    enabled: Boolean(credentials && item?.Type === "Episode" && item.SeriesId && deferredSectionsReady),
  });
  const seriesSeasonsQuery = useQuery({
    queryKey: ["item-seasons", item?.Id],
    queryFn: () => getSeasons(credentials!, item!.Id!),
    enabled: Boolean(credentials && item?.Type === "Series" && item.Id && deferredSectionsReady),
  });
  const seriesFirstSeason = useMemo(() => {
    if (item?.Type !== "Series") return undefined;
    const seasons = [...(seriesSeasonsQuery.data ?? [])].sort(
      (left, right) =>
        (left.IndexNumber ?? Number.MAX_SAFE_INTEGER) -
        (right.IndexNumber ?? Number.MAX_SAFE_INTEGER),
    );
    return (
      seasons.find((season) => season.IndexNumber === 1) ??
      seasons.find((season) => season.IndexNumber !== 0) ??
      seasons[0]
    );
  }, [item?.Type, seriesSeasonsQuery.data]);
  const seriesFirstEpisodesQuery = useQuery({
    queryKey: ["item-episodes", item?.Id, seriesFirstSeason?.Id],
    queryFn: () =>
      getEpisodes(credentials!, item!.Id!, seriesFirstSeason!.Id!),
    enabled: Boolean(
      credentials &&
        item?.Type === "Series" &&
        item.Id &&
        seriesFirstSeason?.Id &&
        deferredSectionsReady,
    ),
  });
  const seriesStartEpisode = useMemo(() => {
    if (item?.Type !== "Series") return undefined;
    const episodes = [...(seriesFirstEpisodesQuery.data ?? [])].sort(
      (left, right) =>
        (left.IndexNumber ?? Number.MAX_SAFE_INTEGER) -
        (right.IndexNumber ?? Number.MAX_SAFE_INTEGER),
    );
    return (
      episodes.find((episode) => episode.IndexNumber === 1) ?? episodes[0]
    );
  }, [item?.Type, seriesFirstEpisodesQuery.data]);
  const seriesStartItemQuery = useQuery({
    queryKey: ["item", seriesStartEpisode?.Id],
    queryFn: () => getItem(credentials!, seriesStartEpisode!.Id!),
    enabled: Boolean(
      credentials &&
        item?.Type === "Series" &&
        seriesStartEpisode?.Id &&
        deferredSectionsReady,
    ),
  });
  const sourceItem =
    item?.Type === "Series" ? seriesStartItemQuery.data : item;
  const detailsItem = useMemo(() => {
    const parentSeries = seriesArtworkQuery.data;
    if (item?.Type !== "Episode" || !parentSeries) return item;
    const seenPeople = new Set<string>();
    const mergedPeople = [
      ...(item.People ?? []),
      ...(parentSeries.People ?? []),
    ].filter((person) => {
      const key = `${person.Id ?? person.Name}:${person.Type ?? ""}:${person.Role ?? ""}`;
      if (seenPeople.has(key)) return false;
      seenPeople.add(key);
      return true;
    });
    return {
      ...item,
      Genres: item.Genres?.length ? item.Genres : parentSeries.Genres,
      GenreItems: item.GenreItems?.length
        ? item.GenreItems
        : parentSeries.GenreItems,
      Tags: item.Tags?.length ? item.Tags : parentSeries.Tags,
      Studios: item.Studios?.length ? item.Studios : parentSeries.Studios,
      ProductionLocations: item.ProductionLocations?.length
        ? item.ProductionLocations
        : parentSeries.ProductionLocations,
      OfficialRating: item.OfficialRating || parentSeries.OfficialRating,
      People: mergedPeople,
    } satisfies JellyfinItem;
  }, [item, seriesArtworkQuery.data]);
  const playbackQuery = useQuery({
    queryKey: playerSourceCatalogQueryKey(
      sourceItem?.Id ?? route.params.itemId,
    ),
    queryFn: () =>
      getPlayerPlaybackInfo(
        credentials!,
        sourceItem?.Id ?? route.params.itemId,
      ),
    enabled: false,
    retry: 0,
    staleTime: PLAYER_SOURCE_CATALOG_STALE_MS,
  });
  const similarItemId =
    item?.Type === "Episode" && item.SeriesId
      ? item.SeriesId
      : route.params.itemId;
  const similarPageSize = 24;
  const similarQuery = useInfiniteQuery({
    queryKey: ["item-similar", similarItemId],
    queryFn: ({ pageParam }) =>
      getSimilarItems(
        credentials!,
        similarItemId,
        similarPageSize,
        pageParam,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) =>
      lastPage.length < similarPageSize
        ? undefined
        : pages.reduce((count, page) => count + page.length, 0),
    enabled: Boolean(credentials && item && deferredSectionsReady),
  });
  const [accent, setAccent] = useState("#e50914");
  const [accentReady, setAccentReady] = useState(false);
  const [tab, setTab] = useState<"Episodes" | "More Like This" | "Details">(
    "More Like This",
  );
  const [similarPostersReady, setSimilarPostersReady] = useState(false);
  const prefs = useNativePreferences();
  const bottomNavInset = useBottomNavInset();
  const [sourceMenuOpen, setSourceMenuOpen] = useState(false);
  const [sourceMenuMounted, setSourceMenuMounted] = useState(false);

  const [sourceMenuPosition, setSourceMenuPosition] = useState({
    left: 18,
    top: 0,
    width: 280,
    height: 286,
  });
  const [selectedSourceId, setSelectedSourceId] = useState("");
  const [sourceSelectionExplicit, setSourceSelectionExplicit] = useState(false);
  const [played, setPlayed] = useState(false);
  const [playStatePending, setPlayStatePending] = useState(false);
  const backdropOpacity = useRef(new Animated.Value(0)).current;
  const backdropScale = useRef(new Animated.Value(1.006)).current;
  const accentContentOpacity = useRef(new Animated.Value(0)).current;
  const heroContentProgress = useRef(new Animated.Value(0)).current;
  const panelContentProgress = useRef(new Animated.Value(1)).current;
  const sourceMenuProgress = useRef(new Animated.Value(0)).current;
  const itemScreenRef = useRef<View>(null);
  const sourceTriggerRef = useRef<View>(null);
  const sourceButtonRef = useRef<View & { focus?: () => void }>(null);
  const restoreSourceFocusRef = useRef(false);
  useEffect(() => {
    let current = true;
    const artworkItem =
      item?.Type === "Episode"
        ? ({
            ...(seriesArtworkQuery.data ?? {}),
            Id: item.ParentBackdropItemId ?? item.SeriesId,
            BackdropImageTags:
              item.ParentBackdropImageTags ??
              seriesArtworkQuery.data?.BackdropImageTags,
          } as JellyfinItem)
        : item;
    if (!credentials || !artworkItem)
      return () => {
        current = false;
      };
    const kind = "Backdrop" as const;
    const cached = getCachedImageAccent(artworkItem, kind);
    if (cached) {
      setAccent(cached);
      setAccentReady(true);
      return () => {
        current = false;
      };
    }
    setAccent("#737373");
    setAccentReady(false);
    void getImageAccent(credentials, artworkItem, kind)
      .then((value) => {
        if (current) {
          setAccent(value);
          setAccentReady(true);
        }
      })
      .catch(() => {
        if (current) {
          setAccent("#e50914");
          setAccentReady(true);
        }
      });
    return () => {
      current = false;
    };
  }, [credentials, item, seriesArtworkQuery.data]);
  useEffect(() => {
    const type = itemQuery.data?.Type;
    setTab(
      type === "Series" || type === "Episode" ? "Episodes" : "More Like This",
    );
    setSourceMenuOpen(false);
    setSelectedSourceId("");
    setSourceSelectionExplicit(false);
    // Keep the detail scene present while the backdrop/image source settles.
    // Starting transparent here creates a black flash during route entry and
    // when preview metadata is replaced by the full item response.
    backdropOpacity.setValue(1);
    backdropScale.setValue(prefs.reduceMotion || televisionRuntime ? 1 : 1.006);
    heroContentProgress.setValue(prefs.reduceMotion ? 1 : 0);
    Animated.timing(heroContentProgress, {
      toValue: 1,
      duration: prefs.reduceMotion ? 0 : 130,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [
    backdropOpacity,
    backdropScale,
    heroContentProgress,
    itemQuery.data?.Id,
    itemQuery.data?.Type,
    prefs.reduceMotion,
  ]);
  useEffect(() => {
    setPlayed(itemQuery.data?.UserData?.Played ?? false);
  }, [itemQuery.data?.UserData?.Played]);
  useEffect(() => {
    setSimilarPostersReady(false);
  }, [similarItemId]);
  useEffect(() => {
    if (!televisionRuntime) return;
    setDeferredSectionsReady(false);
    // Queue non-critical shelves behind the native route animation. This
    // avoids competing with the transition's first frames without removing
    // the polished TV motion or delaying content on non-TV platforms.
    let task: { cancel: () => void } | null = null;
    const timer = setTimeout(() => {
      task = InteractionManager.runAfterInteractions(() => {
        setDeferredSectionsReady(true);
      });
    }, 500);
    return () => {
      clearTimeout(timer);
      task?.cancel();
    };
  }, [item?.Id, televisionRuntime]);
  useEffect(() => {
    panelContentProgress.stopAnimation();
    panelContentProgress.setValue(prefs.reduceMotion ? 1 : 0);
    Animated.timing(panelContentProgress, {
      toValue: 1,
      duration:
        prefs.reduceMotion ? 0 : tab === "More Like This" ? 160 : 130,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [panelContentProgress, prefs.reduceMotion, tab]);
  useEffect(() => {
    if (sourceMenuOpen) setSourceMenuMounted(true);
  }, [sourceMenuOpen]);
  useEffect(() => {
    if (!sourceMenuMounted) return;
    sourceMenuProgress.stopAnimation();
    if (sourceMenuOpen) sourceMenuProgress.setValue(0);
    Animated.timing(sourceMenuProgress, {
      toValue: sourceMenuOpen ? 1 : 0,
      duration: prefs.reduceMotion ? 0 : sourceMenuOpen ? 120 : 90,
      easing: sourceMenuOpen
        ? Easing.out(Easing.cubic)
        : Easing.in(Easing.quad),
      useNativeDriver: true,
    }).start(({ finished }) => {
      if (!finished || sourceMenuOpen) return;
      setSourceMenuMounted(false);
      if (restoreSourceFocusRef.current) {
        restoreSourceFocusRef.current = false;
        setTimeout(() => sourceButtonRef.current?.focus?.(), 0);
      }
    });
  }, [prefs.reduceMotion, sourceMenuMounted, sourceMenuOpen, sourceMenuProgress]);
  useEffect(() => {
    if (!accentReady) {
      // The accent is visual enrichment; it must not blank the hero copy
      // while a newly navigated detail page resolves its artwork.
      accentContentOpacity.setValue(1);
      return;
    }
    Animated.timing(accentContentOpacity, {
      toValue: 1,
      duration: prefs.reduceMotion ? 0 : 90,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [accentContentOpacity, accentReady, prefs.reduceMotion]);
  const accentRgb = useMemo(
    () => getColorChannels(accent).join(","),
    [accent],
  );
  const activeAccent = useMemo(() => {
    const channels = getColorChannels(accent);
    return `rgb(${channels.map((channel) => Math.round(channel * 0.92 + 255 * 0.08)).join(",")})`;
  }, [accent]);
  const accentPresentation = useMemo(
    () => getAccentPresentation(accent),
    [accent],
  );
  // Details should not probe every source just because the page was opened.
  // Prefer cached PlaybackInfo when Player or an intentional prefetch already
  // prepared it, otherwise present the source catalog returned with the item.
  const {
    rankedSources,
    source: selectedSource,
  } = usePlayerSources({
    itemSources: playbackQuery.data?.MediaSources ?? sourceItem?.MediaSources,
    selectedSourceId,
    setSelectedSourceId,
    preferenceScope: item?.SeriesId ?? item?.Id ?? route.params.itemId,
    preferredAudioLanguage: user?.Configuration?.AudioLanguagePreference ?? "",
    allowAutomaticPinnedSelection: true,
  });
  const orderedSources = rankedSources.map((entry) => entry.source);
  const similar = useMemo(() => {
    const seen = new Set<string>();
    return (similarQuery.data?.pages ?? []).flatMap((page) =>
      page.filter((candidate) => {
        const key = candidate.Id ?? "";
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      }),
    );
  }, [similarQuery.data?.pages]);
  if (!item)
    return (
      <SafeAreaView style={styles.safe}>
        <View style={styles.itemColdShell}>
          <SkeletonBlock style={styles.itemColdBackdrop} />
          <View style={styles.itemColdCopy}>
            <SkeletonBlock style={styles.itemColdTitle} />
            <SkeletonBlock style={styles.itemColdMeta} />
            <SkeletonBlock style={styles.itemColdOverview} />
            <SkeletonBlock style={styles.itemColdButton} />
          </View>
        </View>
        <MatureTopNav navigation={navigation} active="Item" overlay />
      </SafeAreaView>
    );
  if (
    item.Type === "MusicAlbum" ||
    item.Type === "MusicArtist" ||
    item.Type === "Playlist"
  )
    return <MusicItemView item={item} navigation={navigation} />;
  const episodic = item.Type === "Series" || item.Type === "Episode";
  const tabs: Array<"Episodes" | "More Like This" | "Details"> = episodic
    ? ["Episodes", "More Like This", "Details"]
    : ["More Like This", "Details"];
  const preferredHeroHeight = compact
    ? Math.max(716, height * 0.84)
    : height <= 660
      ? Math.max(305, Math.min(350, height * 0.52))
      : height <= 760
        ? Math.max(340, Math.min(410, height * 0.54))
        : Math.max(500, Math.min(620, height * 0.61));
  const heroHeight = compact
    ? preferredHeroHeight
    : Math.min(
        preferredHeroHeight,
        Math.max(
          280,
          height -
            60 -
            (episodic ? getEpisodePanelMinimumHeight(width) : 270),
        ),
      );
  const detailsPanelHeight = Math.max(0, height - heroHeight - 60);
  // A 2 GB TV does not benefit from decoding a backdrop wider than the
  // display. Keep the source sharp at TV viewing distance while avoiding a
  // large transient bitmap during route entry.
  const backdropRequestWidth = televisionRuntime
    ? Math.min(1280, Math.round(width))
    : Math.round(width * 1.2);
  const similarCardWidth = getSimilarCardWidth(
    compact,
    width,
    detailsPanelHeight,
  );
  const sourceWidth = compact ? Math.max(280, width - 36) : 280;
  const probingWithoutSources =
    playbackQuery.isLoading && orderedSources.length === 0;
  const resolvingItemSources =
    item?.Type !== "Series" &&
    orderedSources.length === 0 &&
    (itemQuery.isFetching || probingWithoutSources);
  const artworkItem =
    item.Type === "Episode"
      ? ({
          ...(seriesArtworkQuery.data ?? {}),
          Id: item.ParentBackdropItemId ?? item.SeriesId,
          BackdropImageTags:
            item.ParentBackdropImageTags ??
            seriesArtworkQuery.data?.BackdropImageTags,
        } as JellyfinItem)
      : item;
  const canResume =
    item.Type !== "Series" &&
    (item.UserData?.PlaybackPositionTicks ?? 0) > 0;
  const primaryPlaybackItemId =
    item.Type === "Series" ? seriesStartEpisode?.Id : item.Id;
  const resolvingSeriesStart =
    item.Type === "Series" &&
    (seriesSeasonsQuery.isLoading ||
      (Boolean(seriesFirstSeason?.Id) && seriesFirstEpisodesQuery.isLoading));
  const primaryPlaybackUnavailable =
    item.Type === "Series"
      ? !primaryPlaybackItemId
      : !resolvingItemSources && orderedSources.length === 0;
  const episodeCode =
    item.Type === "Episode" && item.ParentIndexNumber != null && item.IndexNumber != null
      ? `S${item.ParentIndexNumber} E${item.IndexNumber}`
      : "";
  const displayTitle =
    item.Type === "Episode" && episodeCode
      ? (item.Name ?? "Untitled").replace(/^S\d+E\d+\s*[-:·]\s*/i, "")
      : (item.Name ?? "Untitled");
  const directors =
    item.People?.filter(
      (person) => person.Type === "Director" && person.Name,
    ) ?? [];
  const closeSourceMenu = (restoreFocus: boolean) => {
    restoreSourceFocusRef.current = restoreFocus;
    setSourceMenuOpen(false);
  };
  const openSourceMenu = () => {
    if (sourceMenuOpen) {
      closeSourceMenu(true);
      return;
    }
    const positionMenu = (
      anchorX: number,
      anchorY: number,
      anchorWidth: number,
      anchorHeight: number,
    ) => {
      const optionHeight = televisionRuntime ? 48 : 36;
      const maxMenuHeight = televisionRuntime ? 384 : 286;
      const menuWidth = compact ? anchorWidth : Math.min(520, width - 24);
      const menuLeft = Math.max(12, Math.min(anchorX, width - menuWidth - 12));
      const menuHeight = Math.min(
        maxMenuHeight,
        Math.max(optionHeight, orderedSources.length * optionHeight),
      );
      const below = anchorY + anchorHeight + 8;
      setSourceMenuPosition({
        left: menuLeft,
        top:
          below + menuHeight <= height - 12
            ? below
            : Math.max(12, anchorY - menuHeight - 8),
        width: menuWidth,
        height: menuHeight,
      });
      setSourceMenuOpen(true);
    };
    if (itemScreenRef.current) {
      sourceTriggerRef.current?.measureLayout(
        itemScreenRef.current,
        positionMenu,
        () => undefined,
      );
      return;
    }
    sourceTriggerRef.current?.measureInWindow((x, y, triggerWidth, triggerHeight) => {
      positionMenu(
        x * referenceScale,
        y * referenceScale,
        triggerWidth * referenceScale,
        triggerHeight * referenceScale,
      );
    });
  };
  const togglePlayed = async () => {
    if (!credentials || !item.Id || playStatePending) return;
    const next = !played;
    setPlayed(next);
    setPlayStatePending(true);
    try {
      if (next) await markItemPlayed(credentials, item.Id);
      else await markItemUnplayed(credentials, item.Id);
      void itemQuery.refetch();
      void queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          const root = String(queryKey[0] ?? "");
          return root === "home" || root.startsWith("catalog") || root.startsWith("item-");
        },
      });
    } catch {
      setPlayed(!next);
    } finally {
      setPlayStatePending(false);
    }
  };
  const sourceControl = orderedSources.length ? (
    <View
      ref={sourceTriggerRef}
      collapsable={false}
      style={[
        styles.sourceControl,
        compact && styles.sourceControlCompact,
      ]}
    >
      <FocusablePressable
        ref={sourceButtonRef}
        accessibilityLabel="Choose media version"
        accessibilityState={{ expanded: sourceMenuOpen }}
        onPress={openSourceMenu}
        style={(focused, pressed, hovered) => [
          styles.sourceButton,
          compact && styles.sourceButtonCompact,
          televisionRuntime && styles.sourceButtonTV,
          { borderColor: accent, backgroundColor: "#171717" },
          focused && styles.focused,
          pressed && styles.pressed,
        ]}
      >
        {(focused, hovered) => (
          <>
            <Svg
              pointerEvents="none"
              width={sourceWidth}
              height={televisionRuntime ? 50 : 46}
              style={styles.sourceButtonTint}
            >
              <Defs>
                <LinearGradient
                  id="source-trigger"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <Stop
                    offset="0"
                    stopColor={accent}
                    stopOpacity={focused || hovered ? 0.36 : 0.24}
                  />
                  <Stop
                    offset="1"
                    stopColor={accent}
                    stopOpacity={focused || hovered ? 0.18 : 0.12}
                  />
                </LinearGradient>
              </Defs>
              <Rect
                width={sourceWidth}
                height={televisionRuntime ? 50 : 46}
                fill="url(#source-trigger)"
              />
            </Svg>
            <Text numberOfLines={1} style={[styles.sourceText, televisionRuntime && styles.sourceTextTV]}>
              {getPlaybackSourceLabel(selectedSource)}
            </Text>
            <ChevronDown
              color={colors.text}
              size={10}
              style={sourceMenuOpen ? styles.chevronOpen : undefined}
            />
          </>
        )}
      </FocusablePressable>
    </View>
  ) : null;
  const adminActions =
    user?.Policy?.IsAdministrator && credentials ? (
      <ItemAdminActions
        accent={accent}
        compact={compact}
        credentials={credentials}
        item={item}
        onDeleted={() => navigation.goBack()}
        onUpdated={() => void itemQuery.refetch()}
      />
    ) : null;
  return (
    <SafeAreaView ref={itemScreenRef} style={styles.safe} edges={[]}>
      {credentials && similar.length ? (
        <SimilarPosterPreloader
          items={similar}
          credentials={credentials}
          cardWidth={similarCardWidth}
          onReady={() => setSimilarPostersReady(true)}
        />
      ) : null}
      <FlatList
        scrollEnabled={compact}
        data={[{ key: "content" }]}
        contentContainerStyle={bottomNavInset ? { paddingBottom: bottomNavInset } : undefined}
        keyExtractor={(entry) => entry.key}
        renderItem={() => (
          <>
            <View style={[styles.hero, compact ? styles.heroCompact : { height: heroHeight }]}>
              {artworkItem ? (
                <Animated.Image
                  source={imageSource(
                    credentials!,
                    artworkItem,
                    "Backdrop",
                    backdropRequestWidth,
                  )}
                  onLoad={() => {
                    Animated.parallel([
                      Animated.timing(backdropOpacity, {
                        toValue: 1,
                        duration: prefs.reduceMotion ? 0 : 180,
                        easing: Easing.out(Easing.cubic),
                        useNativeDriver: true,
                      }),
                      ...(televisionRuntime ? [] : [Animated.timing(backdropScale, {
                        toValue: 1,
                        duration: prefs.reduceMotion ? 0 : 180,
                        easing: Easing.out(Easing.cubic),
                        useNativeDriver: true,
                      })]),
                    ]).start();
                  }}
                  resizeMode="cover"
                  resizeMethod={Platform.OS === "android" ? "resize" : undefined}
                  style={[
                    styles.backdrop,
                    {
                      opacity: backdropOpacity,
                      transform: [{ scale: backdropScale }],
                    },
                  ]}
                />
              ) : null}
              <Svg
                pointerEvents="none"
                width={width}
                height={compact ? "100%" : heroHeight}
                style={styles.backdropShade}
              >
                <Defs>
                  <LinearGradient id="item-bottom" x1="0" y1="0" x2="0" y2="1">
                    <Stop offset="0" stopColor="#141414" stopOpacity="0" />
                    <Stop offset="0.56" stopColor="#141414" stopOpacity="0.6" />
                    <Stop offset="1" stopColor="#141414" />
                  </LinearGradient>
                  <LinearGradient id="item-side" x1="0" y1="0" x2="1" y2="0">
                    <Stop offset="0" stopColor="#141414" />
                    <Stop
                      offset="0.34"
                      stopColor="#141414"
                      stopOpacity="0.82"
                    />
                    <Stop
                      offset="0.68"
                      stopColor="#141414"
                      stopOpacity="0.18"
                    />
                    <Stop offset="1" stopColor="#141414" stopOpacity="0" />
                  </LinearGradient>
                </Defs>
                <Rect
                  width={width}
                  height={compact ? "100%" : heroHeight}
                  fill="url(#item-bottom)"
                />
                <Rect
                  width={width}
                  height={compact ? "100%" : heroHeight}
                  fill="url(#item-side)"
                />
              </Svg>
              <View pointerEvents="none" style={styles.heroBottomSeal} />
              <Animated.View
                style={[
                  styles.heroContent,
                  compact && styles.heroContentCompact,
                  !compact && episodic && styles.heroContentEpisodic,
                  {
                    opacity: Animated.multiply(
                      accentContentOpacity,
                      heroContentProgress,
                    ),
                    transform: [
                      {
                        translateY: heroContentProgress.interpolate({
                          inputRange: [0, 1],
                          outputRange: [4, 0],
                        }),
                      },
                    ],
                  },
                ]}
              >
                <View style={styles.copy}>
                  {item.Type === "Episode" && item.SeriesId ? (
                    <View style={styles.episodeEyebrow}>
                      <FocusablePressable
                        accessibilityLabel={`Open ${item.SeriesName ?? "series"}`}
                        onPress={() =>
                          navigation.navigate("Item", {
                            itemId: item.SeriesId!,
                            transition: "none",
                            ...(seriesArtworkQuery.data
                              ? { previewItem: seriesArtworkQuery.data }
                              : {}),
                          })
                        }
                        style={(focused, pressed) => [pressed && styles.pressed]}
                      >
                        {(focused, hovered) => (
                          <Text
                            numberOfLines={1}
                            style={[
                              styles.seriesLink,
                              { color: accent },
                              (focused || hovered) && styles.seriesLinkActive,
                            ]}
                          >
                            {item.SeriesName ?? "Series"}
                          </Text>
                        )}
                      </FocusablePressable>
                      {episodeCode ? (
                        <View
                          style={[
                            styles.episodeCode,
                            {
                              borderColor: `rgba(${accentRgb},0.42)`,
                              backgroundColor: `rgba(${accentRgb},0.12)`,
                            },
                          ]}
                        >
                          <Text style={styles.episodeCodeText}>{episodeCode}</Text>
                        </View>
                      ) : null}
                    </View>
                  ) : null}
                  <Text
                    style={[
                      styles.title,
                      wide && styles.titleWide,
                      compact && styles.titleCompact,
                    ]}
                  >
                    {displayTitle}
                  </Text>
                  <View
                    style={[styles.metadata, compact && styles.metadataCompact]}
                  >
                    {item.ProductionYear ? (
                      <Text style={styles.meta}>{item.ProductionYear}</Text>
                    ) : null}
                    {item.OfficialRating ? (
                      <Text style={styles.rating}>{item.OfficialRating}</Text>
                    ) : null}
                    {!compact && item.Genres?.[0] ? (
                      <Text style={styles.meta}>{item.Genres[0]}</Text>
                    ) : null}
                    {item.RunTimeTicks ? (
                      <Text style={styles.meta}>
                        {runtime(item.RunTimeTicks)}
                      </Text>
                    ) : null}
                    {item.CommunityRating ? (
                      <Text style={styles.meta}>
                        <Text style={styles.star}>★</Text>{" "}
                        {item.CommunityRating.toFixed(1)}
                      </Text>
                    ) : null}
                  </View>
                  <View
                    style={[styles.actions, compact && styles.actionsCompact]}
                  >
                    <ItemActionPressable
                      reduceMotion={prefs.reduceMotion}
                      fill={compact}
                      accessibilityLabel={`${canResume ? "Resume" : "Play"} ${item.Name ?? "item"}`}
                      disabled={primaryPlaybackUnavailable}
                      onPress={() => {
                        if (!primaryPlaybackItemId) return;
                        navigation.navigate("Player", {
                          itemId: primaryPlaybackItemId,
                          ...(sourceSelectionExplicit && selectedSource?.Id
                            ? { mediaSourceId: selectedSource.Id }
                            : {}),
                        });
                      }}
                      style={(focused, pressed, hovered) => [
                        styles.playButton,
                        compact && styles.playButtonCompact,
                        {
                          backgroundColor:
                            focused || hovered ? activeAccent : accent,
                          borderColor: accent,
                        },
                        (focused || hovered) && styles.actionActive,
                        resolvingItemSources && styles.actionDisabled,
                        primaryPlaybackUnavailable &&
                          styles.actionDisabled,
                        focused && styles.focused,
                        pressed && styles.pressed,
                      ]}
                    >
                      <View
                        pointerEvents="none"
                        style={styles.playTopHighlight}
                      />
                      <View
                        pointerEvents="none"
                        style={styles.playBottomInset}
                      />
                      <Play
                        color={accentPresentation.textColor}
                        fill={accentPresentation.textColor}
                        size={compact ? 16 : 20}
                      />
                      <Text
                        numberOfLines={1}
                        style={[
                          styles.playText,
                          { color: accentPresentation.textColor },
                          compact && styles.playTextCompact,
                        ]}
                      >
                        {resolvingSeriesStart
                          ? "Finding first episode..."
                          : item.Type === "Series"
                            ? primaryPlaybackItemId
                              ? "Play"
                              : "No episodes available"
                            : resolvingItemSources
                          ? "Finding sources..."
                          : orderedSources.length
                            ? canResume
                              ? "Resume"
                              : "Play"
                            : "No playable sources"}
                      </Text>
                    </ItemActionPressable>
                    {canResume ? (
                      <ItemActionPressable
                        reduceMotion={prefs.reduceMotion}
                        fill={compact}
                        accessibilityLabel={`Start ${item.Name ?? "item"} over`}
                        onPress={() => {
                          if (selectedSource?.Id) {
                            queryClient.removeQueries({
                              queryKey: playerPlaybackInfoQueryKey(
                                item.Id!,
                                selectedSource.Id,
                              ),
                              exact: true,
                            });
                          }
                          navigation.navigate("Player", {
                            itemId: item.Id!,
                            ...(sourceSelectionExplicit && selectedSource?.Id
                              ? { mediaSourceId: selectedSource.Id }
                              : {}),
                            startOver: true,
                          });
                        }}
                        style={(focused, pressed, hovered) => [
                          styles.startOverButton,
                          compact && styles.startOverButtonCompact,
                          (focused || hovered) && styles.startOverActive,
                          focused && styles.focused,
                          pressed && styles.pressed,
                        ]}
                      >
                        <RotateCcw color={colors.text} size={compact ? 15 : 18} />
                        <Text
                          numberOfLines={1}
                          style={[
                            styles.startOverText,
                            compact && styles.startOverTextCompact,
                          ]}
                        >
                          Start Over
                        </Text>
                      </ItemActionPressable>
                    ) : null}
                    {
                      <ItemActionPressable
                        reduceMotion={prefs.reduceMotion}
                        fill={compact}
                        accessibilityLabel={
                          played ? "Mark as unplayed" : "Mark as played"
                        }
                        accessibilityState={{
                          checked: played,
                          disabled: playStatePending,
                        }}
                        disabled={playStatePending}
                        onPress={() => void togglePlayed()}
                        style={(focused, pressed, hovered) => [
                          styles.playStateButton,
                          compact && styles.playStateButtonCompact,
                          {
                            borderColor:
                              focused || hovered || played
                                ? accent
                                : "rgba(255,255,255,0.24)",
                            backgroundColor: played
                              ? `rgba(${accentRgb},0.24)`
                              : "rgba(255,255,255,0.08)",
                          },
                          (focused || hovered) && styles.startOverActive,
                          playStatePending && styles.actionDisabled,
                          pressed && styles.pressed,
                        ]}
                      >
                        {!compact ? (
                          played ? (
                            <CircleCheck color={colors.text} size={18} />
                          ) : (
                            <Circle color={colors.text} size={18} />
                          )
                        ) : null}
                        <Text
                          numberOfLines={1}
                          style={[
                            styles.startOverText,
                            compact && styles.startOverTextCompact,
                          ]}
                        >
                          {played ? "Mark as Unplayed" : "Mark as Played"}
                        </Text>
                      </ItemActionPressable>
                    }
                    {!compact ? sourceControl : null}
                    {!compact ? adminActions : null}
                  </View>
                  {compact && (sourceControl || adminActions) ? (
                    <View style={styles.actionsSecondaryCompact}>
                      {sourceControl}
                      {adminActions}
                    </View>
                  ) : null}
                  <Text
                    style={[styles.overview, compact && styles.overviewCompact]}
                  >
                    {item.Overview ?? ""}
                  </Text>
                </View>
                {!compact && directors.length ? (
                  <View style={styles.credits}>
                    <Text style={styles.creditHeading}>Directors:</Text>
                    {directors.map((person, index) => (
                      <FocusablePressable
                        key={person.Id ?? `${person.Name}-${index}`}
                        onPress={() =>
                          person.Id &&
                          navigation.navigate("Person", { personId: person.Id })
                        }
                        style={(focused, pressed, hovered) => [
                          (focused || hovered) && styles.detailLinkActive,
                          pressed && styles.pressed,
                        ]}
                      >
                        <Text style={styles.creditName}>{person.Name}</Text>
                      </FocusablePressable>
                    ))}
                  </View>
                ) : null}
              </Animated.View>
            </View>
            <View style={styles.tabs}>
              <FlatList
                horizontal
                data={tabs}
                keyExtractor={(value) => value}
                contentContainerStyle={[
                  styles.tabRow,
                  compact && styles.tabRowCompact,
                ]}
                renderItem={({ item: label }) => (
                  <FocusablePressable
                    onPress={() => {
                      closeSourceMenu(false);
                      setTab(label);
                    }}
                    style={(focused, pressed) => [
                      styles.tab,
                      pressed && styles.pressed,
                    ]}
                  >
                    {(focused, hovered) => (
                      <>
                        <Text
                          style={[
                            styles.tabText,
                            tab === label && styles.tabTextActive,
                            (focused || hovered) && styles.tabTextActive,
                          ]}
                        >
                          {label}
                        </Text>
                        {tab === label ? (
                          <View
                            style={[
                              styles.tabAccent,
                              { backgroundColor: accent },
                            ]}
                          />
                        ) : null}
                      </>
                    )}
                  </FocusablePressable>
                )}
              />
            </View>
            <View
              style={[
                styles.panel, compact && styles.panelCompact,
                !compact && {
                  height: detailsPanelHeight,
                  minHeight: detailsPanelHeight,
                  maxHeight: detailsPanelHeight,
                },
                { borderTopColor: `rgba(${accentRgb},0.34)` },
              ]}
            >
              {televisionRuntime ? null : (
                <Svg
                  pointerEvents="none"
                  width={width}
                  height={280}
                  style={styles.panelGlow}
                >
                  <Defs>
                    <RadialGradient
                      id="panel-accent"
                      cx="18%"
                      cy="0"
                      rx="62%"
                      ry="80%"
                    >
                      <Stop
                        offset="0"
                        stopColor={`rgb(${accentRgb})`}
                        stopOpacity="0.12"
                      />
                      <Stop
                        offset="1"
                        stopColor={`rgb(${accentRgb})`}
                        stopOpacity="0"
                      />
                    </RadialGradient>
                  </Defs>
                  <Rect width={width} height="280" fill="url(#panel-accent)" />
                </Svg>
              )}
              <Animated.View
                style={[
                  styles.panelContentMotion,
                  {
                    opacity: panelContentProgress.interpolate({
                      inputRange: [0, 1],
                      outputRange: [0.45, 1],
                    }),
                    transform: [
                      {
                        translateY: panelContentProgress.interpolate({
                          inputRange: [0, 1],
                          outputRange: [tab === "More Like This" ? 5 : 3, 0],
                        }),
                      },
                    ],
                  },
                ]}
              >
              {televisionRuntime && !deferredSectionsReady ? null : tab === "Episodes" &&
              episodic &&
              (item.Type === "Episode" ? item.SeriesId : item.Id) ? (
                <SeasonEpisodes
                  credentials={credentials!}
                  navigation={navigation}
                  seriesId={
                    (item.Type === "Episode" ? item.SeriesId : item.Id)!
                  }
                  initialSeasonId={
                    item.Type === "Episode" ? item.ParentId : undefined
                  }
                  initialEpisodeId={
                    item.Type === "Episode" ? item.Id : undefined
                  }
                  compact={compact}
                  vertical={compact && prefs.mobileEpisodeList}
                  screenWidth={width}
                  accent={accent}
                  reduceMotion={prefs.reduceMotion}
                  animeLocalization={getAnimeLocalization(item)}
                />
              ) : tab === "More Like This" ? (
                <SimilarItemsPanel
                  items={similar}
                  loading={similarQuery.isLoading}
                  loadingMore={similarQuery.isFetchingNextPage}
                  hasMore={similarQuery.hasNextPage}
                  onLoadMore={() => void similarQuery.fetchNextPage()}
                  credentials={credentials!}
                  navigation={navigation}
                  compact={compact}
                  vertical={compact && prefs.mobileEpisodeList}
                  screenWidth={width}
                  panelHeight={detailsPanelHeight}
                  accent={accent}
                  instantImageReveal={similarPostersReady}
                />
              ) : (
                <View
                  style={[
                    styles.glass,
                    styles.detailsGlass,
                    !compact && styles.detailsGlassViewport,
                    compact && styles.glassCompact,
                  ]}
                >
                  <ItemDetailsPanel
                    item={detailsItem ?? item}
                    compact={compact}
                    screenWidth={width}
                    panelHeight={detailsPanelHeight}
                    isAdministrator={user?.Policy?.IsAdministrator === true}
                    navigation={navigation}
                  />
                </View>
              )}
              </Animated.View>
            </View>
          </>
        )}
        showsVerticalScrollIndicator={false}
        onScrollBeginDrag={() => closeSourceMenu(false)}
        onEndReached={
          compact &&
          tab === "More Like This" &&
          similarQuery.hasNextPage &&
          !similarQuery.isFetchingNextPage
            ? () => void similarQuery.fetchNextPage()
            : undefined
        }
        onEndReachedThreshold={0.55}
      />
      {sourceMenuMounted ? (
        <>
          <Pressable
            focusable={false}
            accessibilityElementsHidden
            onPress={() => closeSourceMenu(true)}
            style={styles.sourceDismiss}
          />
          <Animated.View
            pointerEvents={sourceMenuOpen ? "auto" : "none"}
            style={[
              styles.sourceMenuShell,
              televisionRuntime && styles.sourceMenuShellTV,
              sourceMenuPosition,
              {
                borderColor: `rgba(${accentRgb},0.62)`,
                opacity: sourceMenuProgress,
                transform: [
                  {
                    translateY: sourceMenuProgress.interpolate({
                      inputRange: [0, 1],
                      outputRange: [-6, 0],
                    }),
                  },
                  {
                    scale: sourceMenuProgress.interpolate({
                      inputRange: [0, 1],
                      outputRange: [0.985, 1],
                    }),
                  },
                ],
              },
            ]}
          >
            <Svg
              pointerEvents="none"
              width={sourceMenuPosition.width}
              height={sourceMenuPosition.height}
              style={styles.sourceMenuTint}
            >
              <Defs>
                <LinearGradient id="source-menu" x1="0" y1="0" x2="0" y2="1">
                  <Stop offset="0" stopColor={accent} stopOpacity="0.16" />
                  <Stop offset="1" stopColor={accent} stopOpacity="0.04" />
                </LinearGradient>
              </Defs>
              <Rect
                width={sourceMenuPosition.width}
                height={sourceMenuPosition.height}
                fill="url(#source-menu)"
              />
            </Svg>
            <FlatList<PlaybackSource>
              accessibilityLabel="Media versions"
              data={orderedSources}
              showsVerticalScrollIndicator={false}
              keyExtractor={(source, index) => source.Id ?? `source-${index}`}
              style={[styles.sourceMenu, { maxHeight: sourceMenuPosition.height }]}
              renderItem={({ item: source, index }) => {
                const selected = source.Id === selectedSource?.Id;
                return (
                  <FocusablePressable
                    accessibilityLabel={`Use ${getPlaybackSourceLabel(source, index)}`}
                    accessibilityState={{ selected }}
                    onPress={() => {
                      setSelectedSourceId(source.Id ?? "");
                      setSourceSelectionExplicit(true);
                      closeSourceMenu(true);
                    }}
                    style={(focused, pressed, hovered) => [
                      styles.sourceOption,
                      televisionRuntime && styles.sourceOptionTV,
                      selected && {
                        backgroundColor: `rgba(${accentRgb},0.34)`,
                      },
                      (focused || hovered) && {
                        backgroundColor: `rgba(${accentRgb},0.22)`,
                      },
                      pressed && styles.pressed,
                    ]}
                  >
                    <Text numberOfLines={1} style={[styles.sourceOptionText, televisionRuntime && styles.sourceOptionTextTV]}>
                      {getPlaybackSourceLabel(source, index)}
                    </Text>
                    {selected ? (
                      <Check color={colors.text} size={televisionRuntime ? 18 : 15} strokeWidth={2.5} />
                    ) : null}
                  </FocusablePressable>
                );
              }}
            />
          </Animated.View>
        </>
      ) : null}
      <MatureTopNav navigation={navigation} active="Item" overlay />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: "#0a0a0a" },
  loader: { marginTop: 180 },
  hero: {
    position: "relative",
    justifyContent: "flex-end",
    overflow: "hidden",
  },
  heroCompact: { minHeight: 380 },
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: "auto",
    height: "auto",
  },
  backdropShade: { position: "absolute", top: 0, left: 0 },
  heroBottomSeal: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 2,
    backgroundColor: "#141414",
  },
  heroContent: {
    paddingHorizontal: 56,
    paddingBottom: 48,
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 32,
  },
  heroContentEpisodic: { paddingBottom: 20 },
  heroContentCompact: {
    paddingHorizontal: 18,
    paddingTop: 104,
    paddingBottom: 26,
    alignItems: "flex-start",
  },
  copy: { flex: 1, maxWidth: 780 },
  episodeEyebrow: {
    marginBottom: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  seriesLink: {
    fontFamily: systemSansFont,
    fontSize: 18,
    lineHeight: 23,
    fontWeight: "500",
  },
  seriesLinkActive: { textDecorationLine: "underline" },
  episodeCode: {
    minHeight: 24,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
  },
  episodeCodeText: {
    color: "rgba(255,255,255,0.9)",
    fontFamily: systemSansFont,
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "800",
    letterSpacing: 0.5,
  },
  title: {
    marginBottom: 16,
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 48,
    lineHeight: 48,
    fontWeight: "800",
    letterSpacing: 0,
  },
  titleWide: { fontSize: 60, lineHeight: 60 },
  titleCompact: {
    marginBottom: 8,
    fontSize: 24,
    lineHeight: 26,
    transform: [{ translateY: 0 }],
  },
  metadata: {
    marginBottom: 24,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  metadataCompact: { marginBottom: 16, gap: 8 },
  meta: {
    color: "rgba(255,255,255,0.68)",
    fontFamily: systemSansFont,
    fontSize: 16,
    lineHeight: 20,
    fontWeight: "700",
  },
  rating: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderWidth: 1,
    borderColor: "#4b5563",
    borderRadius: 4,
    color: "rgba(255,255,255,0.68)",
    fontFamily: systemSansFont,
    fontSize: 13,
    lineHeight: 13,
    fontWeight: "700",
  },
  star: { color: colors.brand },
  actions: {
    maxWidth: 780,
    marginBottom: 24,
    flexDirection: "row",
    alignItems: "center",
    gap: 14,
    zIndex: 20,
  },
  actionMotionFill: { flex: 1, minWidth: 0, height: 48, alignSelf: "stretch" },
  actionsCompact: {
    flexDirection: "row",
    alignItems: "stretch",
    flexWrap: "nowrap",
    gap: 6,
    marginBottom: 0,
  },
  actionsSecondaryCompact: {
    marginTop: 10,
    alignSelf: "stretch",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 10,
    zIndex: 30,
  },
  playButton: {
    minWidth: 160,
    height: 52,
    paddingHorizontal: 32,
    borderWidth: 1,
    borderRadius: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOpacity: 0.34,
    shadowRadius: 21,
    shadowOffset: { width: 0, height: 16 },
    elevation: 10,
  },
  playButtonCompact: { flex: 1, minWidth: 0, width: "100%", paddingHorizontal: 10, height: 48, gap: 6 },
  playTopHighlight: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: "rgba(255,255,255,0.22)",
  },
  playBottomInset: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 3,
    backgroundColor: "rgba(5,5,5,0.24)",
  },
  playText: {
    color: "#050505",
    fontFamily: systemSansFont,
    fontSize: 18,
    lineHeight: 28,
    fontWeight: "700",
  },
  playTextCompact: { fontSize: 15, lineHeight: 20 },
  startOverButton: {
    minWidth: 150,
    height: 48,
    paddingHorizontal: 24,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.24)",
    borderRadius: 4,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: "rgba(255,255,255,0.1)",
  },
  startOverButtonCompact: { flex: 1, minWidth: 0, width: "100%", paddingHorizontal: 8, height: 48, gap: 6 },
  playStateButton: {
    minWidth: 158,
    height: 48,
    paddingHorizontal: 24,
    borderWidth: 1,
    borderRadius: 4,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  playStateButtonCompact: { flex: 1, minWidth: 0, width: "100%", paddingHorizontal: 8, height: 48, gap: 6 },
  startOverActive: { backgroundColor: "rgba(255,255,255,0.18)" },
  startOverText: {
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "700",
  },
  startOverTextCompact: { fontSize: 12, lineHeight: 16 },
  sourceControl: { width: 280, zIndex: 30 },
  sourceControlCompact: { alignSelf: "stretch", width: "100%" },
  sourceButton: {
    width: 280,
    height: 46,
    paddingHorizontal: 14,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.26)",
    borderRadius: 8,
    backgroundColor: "#171717",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    overflow: "hidden",
    shadowColor: "#000",
    shadowOpacity: 0.28,
    shadowRadius: 9,
    shadowOffset: { width: 0, height: 4 },
    elevation: 7,
  },
  sourceButtonCompact: { width: "100%" },
  sourceButtonTV: { height: 50, paddingHorizontal: 16 },
  sourceButtonTint: { position: "absolute", top: 0, left: 0 },
  sourceText: {
    flex: 1,
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 15,
    lineHeight: 20,
    fontWeight: "700",
  },
  sourceTextTV: { fontSize: 17, lineHeight: 23 },
  chevronOpen: { transform: [{ rotate: "180deg" }] },
  sourceDismiss: { ...StyleSheet.absoluteFillObject, zIndex: 90 },
  sourceMenuShell: {
    position: "absolute",
    maxHeight: 286,
    borderWidth: 1,
    borderRadius: 6,
    backgroundColor: "#171717",
    overflow: "hidden",
    zIndex: 100,
  },
  sourceMenuShellTV: { maxHeight: 384 },
  sourceMenuTint: { position: "absolute", top: 0, left: 0 },
  sourceMenu: { maxHeight: 286, backgroundColor: "transparent" },
  sourceOption: {
    minHeight: 36,
    paddingHorizontal: 14,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.07)",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  sourceOptionTV: { minHeight: 48, paddingHorizontal: 16 },
  sourceOptionText: {
    flex: 1,
    color: "rgba(255,255,255,0.82)",
    fontFamily: systemSansFont,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "700",
  },
  sourceOptionTextTV: { fontSize: 17, lineHeight: 24 },
  actionActive: { shadowOpacity: 0.38, shadowRadius: 23 },
  actionDisabled: { opacity: 0.48 },
  sourceActive: { backgroundColor: "rgba(255,255,255,0.12)" },
  focused: { borderWidth: 3, borderColor: colors.text },
  pressed: { opacity: 0.78 },
  overview: {
    maxWidth: 820,
    color: "rgba(255,255,255,0.68)",
    fontFamily: systemSansFont,
    fontSize: 18,
    lineHeight: 28,
  },
  overviewCompact: {
    marginBottom: 0,
    position: "relative",
    top: "auto",
    left: 0,
    right: 0,
    fontSize: 18,
    lineHeight: 28,
  },
  credits: { width: 288, paddingBottom: 8 },
  creditHeading: {
    marginBottom: 10,
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "900",
  },
  creditName: {
    color: "rgba(255,255,255,0.68)",
    fontFamily: systemSansFont,
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
  },
  tabs: {
    height: 60,
    marginTop: -1,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.14)",
    backgroundColor: "#141414",
  },
  tabRow: { paddingHorizontal: 56, gap: 32 },
  tabRowCompact: { paddingHorizontal: 18 },
  tab: { height: 59, justifyContent: "center" },
  tabCompact: { height: 44 },
  tabText: {
    color: "rgba(255,255,255,0.52)",
    fontFamily: systemSansFont,
    fontSize: 15,
    lineHeight: 20,
    fontWeight: "900",
    letterSpacing: 0.9,
    textTransform: "uppercase",
  },
  tabTextActive: { color: colors.text },
  tabAccent: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 4,
    borderTopLeftRadius: 999,
    borderTopRightRadius: 999,
  },
  panelCompact: { paddingTop: 0 },
  panel: {
    minHeight: 290,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "rgba(229,9,20,0.34)",
    backgroundColor: "#151515",
    overflow: "hidden",
  },
  panelGlow: { position: "absolute", top: 0, left: 0 },
  panelContentMotion: { flex: 1, minHeight: 0, width: "100%" },
  similarPosterPreloader: {
    position: "absolute",
    width: 1,
    height: 1,
    opacity: 0,
    overflow: "hidden",
  },
  similarPosterPreloadImage: { width: 1, height: 1 },
  similarInteractionRegion: { position: "relative" },
  glass: {
    marginHorizontal: 56,
    marginTop: 12,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.08)",
    borderRadius: 8,
    backgroundColor: "rgba(0,0,0,0.18)",
  },
  glassCompact: { marginHorizontal: 18, marginTop: 4, paddingBottom: 0, paddingTop: 0 },
  similarRow: { paddingTop: 18, paddingHorizontal: 7, gap: 8 },
  similarListViewport: { overflow: "hidden" },
  similarStatus: {
    minHeight: 238,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  similarStatusStack: { flexDirection: "column", gap: 5 },
  similarEmptyTitle: {
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 16,
    lineHeight: 21,
    fontWeight: "800",
  },
  similarStatusText: {
    color: "rgba(255,255,255,0.56)",
    fontFamily: systemSansFont,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "700",
  },
  similarLoadingMore: {
    position: "absolute",
    right: 18,
    bottom: 16,
    minWidth: 118,
    height: 34,
    paddingHorizontal: 12,
    borderRadius: 17,
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 8,
    backgroundColor: "rgba(8,8,8,0.76)",
  },
  similarLoadingMoreText: {
    color: "rgba(255,255,255,0.78)",
    fontFamily: systemSansFont,
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "700",
  },
  similarScrollButton: {
    position: "absolute",
    top: 30,
    zIndex: 20,
    elevation: 20,
    width: 44,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.14)",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(10,10,10,0.9)",
  },
  similarScrollLeft: {
    left: 6,
    borderRadius: 22,
  },
  similarScrollRight: {
    right: 6,
    borderRadius: 22,
  },
  detailsGlass: { marginTop: 6, paddingVertical: 14 },
  detailsGlassViewport: { flex: 1, minHeight: 0, overflow: "hidden" },
  detailsGrid: {
    flex: 1,
    minHeight: 0,
    flexDirection: "row",
    gap: 56,
    overflow: "hidden",
  },
  detailsGridCompact: { flexDirection: "column", gap: 22 },
  detailsMain: { flex: 1, minHeight: 0, overflow: "hidden" },
  detailsSidebar: { width: 280, minHeight: 0, overflow: "hidden" },
  detailGroup: { marginBottom: 10 },
  detailHeading: {
    marginBottom: 5,
    color: colors.text,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "900",
  },
  castGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: 20,
    rowGap: 5,
  },
  castEntry: {
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    borderRadius: 4,
  },
  detailLinkActive: { backgroundColor: "rgba(255,255,255,0.09)" },
  detailLink: {
    color: "rgba(255,255,255,0.82)",
    fontSize: 12,
    lineHeight: 17,
    fontWeight: "700",
  },
  castRole: { color: "rgba(255,255,255,0.45)", fontSize: 10, lineHeight: 14 },
  aboutGrid: {
    marginTop: 10,
    marginBottom: 10,
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: 20,
    rowGap: 6,
  },
  aboutEntry: { minWidth: 180, flexGrow: 1 },
  aboutLabel: {
    marginBottom: 2,
    color: "rgba(255,255,255,0.45)",
    fontSize: 10,
    lineHeight: 13,
    fontWeight: "800",
    textTransform: "uppercase",
  },
  aboutValue: { color: "rgba(255,255,255,0.82)", fontSize: 12, lineHeight: 16 },
  tagRow: { flexDirection: "row", flexWrap: "wrap", gap: 6 },
  detailTag: {
    minHeight: 24,
    maxWidth: 220,
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.12)",
    borderRadius: 6,
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.05)",
  },
  detailTagActive: {
    borderColor: "rgba(255,255,255,0.34)",
    backgroundColor: "rgba(255,255,255,0.12)",
  },
  detailTagText: {
    flexShrink: 1,
    color: "rgba(255,255,255,0.82)",
    fontSize: 10,
    lineHeight: 12,
    fontWeight: "600",
  },
  technicalTags: { opacity: 0.74 },
  sidebarGroup: { marginBottom: 14 },
  sidebarValue: {
    color: "rgba(255,255,255,0.68)",
    fontSize: 12,
    lineHeight: 18,
    fontWeight: "600",
  },
  sidebarOverflow: {
    color: "rgba(255,255,255,0.46)",
    fontSize: 11,
    lineHeight: 18,
    fontWeight: "700",
  },
  episodesPanel: { minHeight: 270 },
  episodeEmptyState: {
    minHeight: 188,
    marginHorizontal: 56,
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.08)",
    borderRadius: 8,
    backgroundColor: "rgba(0,0,0,0.18)",
  },
  episodeEmptyTitle: {
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 16,
    lineHeight: 21,
    fontWeight: "800",
  },
  episodeEmptyText: {
    color: "rgba(255,255,255,0.56)",
    fontFamily: systemSansFont,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "600",
  },
  seasonScroller: { position: "relative", marginHorizontal: 56 },
  rowScrollerCompact: { marginHorizontal: 18 },
  seasonRow: {
    paddingTop: 4,
    paddingBottom: 8,
    gap: 10,
  },
  seasonButton: {
    width: 116,
    minWidth: 116,
    height: 44,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.14)",
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.055)",
    overflow: "hidden",
  },
  seasonButtonSelected: {
    borderColor: "rgba(255,255,255,0.3)",
    backgroundColor: "rgba(255,255,255,0.13)",
  },
  seasonButtonActive: {
    borderColor: "rgba(255,255,255,0.48)",
    backgroundColor: "rgba(255,255,255,0.17)",
  },
  seasonButtonText: {
    color: "rgba(255,255,255,0.78)",
    fontFamily: systemSansFont,
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "800",
  },
  seasonButtonTextSelected: { color: colors.text },
  seasonSelectedInset: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 3,
  },
  seasonScrollButton: {
    position: "absolute",
    top: 4,
    bottom: 8,
    zIndex: 4,
    width: 40,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.14)",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(10,10,10,0.9)",
  },
  seasonScrollLeft: {
    left: 0,
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
  },
  seasonScrollRight: {
    right: 0,
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
  },
  seasonScrollDisabled: { opacity: 0, pointerEvents: "none" },
  episodeScroller: { position: "relative", marginHorizontal: 56 },
  episodeRow: { paddingTop: 6, gap: 10 },
  episodeListColumn: { paddingTop: 6, gap: 4 },
  episodeListRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 6,
    paddingTop: 8,
    paddingBottom: 12,
    borderRadius: 8,
  },
  episodeListRowActive: { backgroundColor: "rgba(255,255,255,0.06)" },
  episodeListThumb: {
    overflow: "hidden",
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: "rgba(255,255,255,0.10)",
    backgroundColor: colors.surfaceRaised,
  },
  episodeListBody: { flex: 1, minWidth: 0, gap: 3 },
  episodeListTitleRow: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 8,
  },
  episodeListTitle: {
    flex: 1,
    minWidth: 0,
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 15,
    lineHeight: 19,
    fontWeight: "700",
  },
  episodeListDurationInline: {
    color: "rgba(255,255,255,0.52)",
    fontFamily: systemSansFont,
    fontSize: 11,
    lineHeight: 14,
    fontWeight: "600",
  },
  episodeListWatched: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: colors.brand,
  },
  episodeListLocalizationBadge: {
    position: "absolute",
    top: 6,
    left: 6,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: "rgba(12,12,14,0.88)",
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.30)",
  },
  episodeListLocalizationText: {
    color: colors.text,
    fontFamily: systemSansFont,
    fontSize: 10,
    lineHeight: 12,
    fontWeight: "800",
    letterSpacing: 0.7,
  },
  episodeListOverview: {
    color: "rgba(255,255,255,0.62)",
    fontFamily: systemSansFont,
    fontSize: 12,
    lineHeight: 17.5,
  },
  episodeListTrack: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 3,
    backgroundColor: "rgba(255,255,255,0.14)",
  },
  episodeListProgress: { height: 3, backgroundColor: colors.brand },
  episodeSkeletonLine: { width: "86%", marginTop: 2 },
  episodeSkeletonImage: {
    borderRadius: 6,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: "rgba(255,255,255,0.08)",
    backgroundColor: "rgba(255,255,255,0.075)",
  },
  episodeSkeletonTitle: {
    width: 118,
    height: 12,
    marginTop: 8,
    borderRadius: 4,
    backgroundColor: "rgba(255,255,255,0.09)",
  },
  seasonLoadingBadge: {
    position: "absolute",
    top: 66,
    zIndex: 12,
    width: 190,
    minHeight: 40,
    paddingHorizontal: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 9,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.14)",
    borderRadius: 999,
    backgroundColor: "rgba(10,10,10,0.88)",
    shadowColor: "#000",
    shadowOpacity: 0.32,
    shadowRadius: 14,
    elevation: 8,
  },
  seasonLoadingText: {
    color: "rgba(255,255,255,0.9)",
    fontFamily: systemSansFont,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "700",
  },
  itemColdShell: {
    flex: 1,
    justifyContent: "flex-end",
    overflow: "hidden",
  },
  itemColdBackdrop: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 0,
  },
  itemColdCopy: {
    width: "88%",
    maxWidth: 680,
    paddingHorizontal: 28,
    paddingBottom: 74,
    gap: 12,
  },
  itemColdTitle: { width: "72%", height: 54, borderRadius: 7 },
  itemColdMeta: { width: 190, height: 14 },
  itemColdOverview: { width: "100%", height: 72 },
  itemColdButton: { width: 126, height: 42, borderRadius: 6 },
  episodeScrollButton: {
    position: "absolute",
    top: 6,
    zIndex: 40,
    elevation: 40,
    width: 44,
    height: 174,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.14)",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(10,10,10,0.9)",
  },
  episodeScrollLeft: {
    left: 0,
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
  },
  episodeScrollRight: {
    right: 0,
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
  },
});
